import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router";
import {
  ArrowLeft, ShieldCheck, FileText, AlertTriangle, ClipboardList,
  CheckCircle2, XCircle, Clock, Plus, Edit2, Trash2,
  ChevronDown, ChevronRight, BarChart2, BookOpen, AlertCircle,
  Upload, Eye, History, Download, ListChecks, FilePlus2, TableProperties,
  Link2, Unlink, GitBranch,
} from "lucide-react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";

// ─── Types ────────────────────────────────────────────────────────────────────

export type ISOTab = "dashboard" | "documents" | "controls" | "risks" | "incidents" | "audits";

interface ISODashboard {
  documents: { total: number; approved: number; draft: number; review: number; overdue: number; dueSoon: number };
  controls:  { total: number; implemented: number; inProgress: number; notStarted: number; notApplicable: number };
  risks:     { total: number; open: number; critical: number };
  incidents: { total: number; open: number };
  audits:    { total: number; planned: number; complete: number };
}

interface ISODocument {
  id: string;
  code: string;
  title: string;
  category: string;
  status: string;       // draft | review | approved | superseded
  owner: string | null;
  version: string;
  reviewDate: string | null;
  notes: string | null;
  content: string | null;
  fileUrl: string | null;
  fileName: string | null;
  reviewFrequency: string;
  lastReviewed: string | null;
  folder: string | null;
  author: string | null;
  reviewer: string | null;
  approver: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ISOAuditFinding {
  id: string;
  auditId: string;
  documentId: string | null;
  findingId: string;
  title: string;
  description: string | null;
  severity: string;                      // minor | major | observation
  status: string;                        // open | closed
  controlId: string | null;
  correctiveAction: string | null;
  correctiveActionStatus: string;        // open | in-progress | closed
  documentCode: string | null;           // joined
  documentTitle: string | null;          // joined
  controlCode: string | null;            // joined
  controlName: string | null;            // joined
  createdAt: string;
  updatedAt: string;
}

interface ISOLinkedRisk {
  id: string;
  riskId: string;
  asset: string;
  threat: string;
  score: number;
  status: string;
  treatment: string;
}

interface ISOLinkedAudit {
  id: string;
  title: string;
  type: string;
  plannedDate: string | null;
  status: string;
  findingCount: number;
}

interface ISODocumentLog {
  id: string;
  document_id: string;
  action: string;
  field: string | null;
  old_value: string | null;
  new_value: string | null;
  createdAt: string;
}

interface ISOReviewAlert {
  id: string;
  code: string;
  title: string;
  category: string;
  status: string;
  reviewDate: string;
}

interface ISOControl {
  id: string;
  controlId: string;
  name: string;
  category: string;
  applicable: boolean;
  status: string;       // not-started | in-progress | implemented | not-applicable
  owner: string | null;
  evidence: string | null;
  justification: string | null;
  updatedAt: string;
}

interface ISORisk {
  id: string;
  riskId: string;
  asset: string;
  threat: string;
  vulnerability: string;
  likelihood: number;
  impact: number;
  score: number;
  treatment: string;    // accept | mitigate | transfer | avoid
  controlMeasure: string | null;
  residualScore: number;
  owner: string | null;
  status: string;       // open | in-treatment | accepted | closed
  createdAt: string;
  updatedAt: string;
}

interface ISOIncident {
  id: string;
  incidentId: string;
  title: string;
  description: string | null;
  severity: string;     // critical | high | medium | low
  status: string;       // open | investigating | resolved | closed
  reporter: string | null;
  assignedTo: string | null;
  occurredAt: string | null;
  resolvedAt: string | null;
  correctiveAction: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ISOAudit {
  id: string;
  type: string;         // internal | external | surveillance
  title: string;
  auditor: string | null;
  scope: string | null;
  plannedDate: string | null;
  actualDate: string | null;
  status: string;       // planned | in-progress | complete
  findings: string | null;
  nonconformities: number;
  observations: number;
  createdAt: string;
  updatedAt: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${fmtDate(iso)} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function cap(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

// Converts plain-text docx content to basic HTML for TipTap.
// Already-HTML content (starts with "<") passes through unchanged.
function plainTextToHtml(text: string | null): string {
  if (!text) return "";
  const t = text.trim();
  if (t.startsWith("<")) return t;
  return t
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`)
    .join("");
}

// Section templates by document category — used to pre-populate empty documents
const SECTION_TEMPLATES: Record<string, string[]> = {
  policy:           ["Purpose", "Scope", "Policy Statement", "Responsibilities", "Non-Compliance", "Related Documents"],
  procedure:        ["Purpose", "Scope", "Procedure", "Responsibilities", "Records", "References"],
  plan:             ["Purpose", "Scope", "Objectives", "Plan Details", "Responsibilities", "Review Schedule"],
  audit:            ["Scope", "Methodology", "Findings", "Nonconformities", "Observations", "Conclusions"],
  minutes:          ["Meeting Details", "Attendees", "Agenda", "Minutes", "Action Items"],
  work_instruction: ["Purpose", "Scope", "Instructions", "References"],
  register:         ["Purpose", "Register", "Review History"],
  log:              ["Log Entries"],
  bcp:              ["Purpose", "Scope", "Recovery Objectives", "Recovery Procedures", "Testing Schedule"],
  context:          ["Overview", "Details", "References"],
  isms:             ["Purpose", "Scope", "Requirements", "Implementation", "References"],
  guide:            ["Purpose", "Scope", "Guide Content", "References"],
  training:         ["Purpose", "Learning Objectives", "Content", "Assessment"],
};

// Parse JSON-sectioned content → { sectionName: htmlContent }
function parseSectionedContent(content: string | null): Record<string, string> | null {
  if (!content) return null;
  try {
    const p = JSON.parse(content);
    if (p._sections) {
      const { _sections: _, ...sections } = p;
      return sections as Record<string, string>;
    }
  } catch { /* not JSON */ }
  return null;
}

// Build JSON-sectioned content from { sectionName: htmlContent }
function buildSectionedContent(sections: Record<string, string>): string {
  return JSON.stringify({ _sections: true, ...sections });
}

// Build template HTML from section list (H2 headings + empty paragraphs)
function buildTemplateHtml(sections: string[]): string {
  return sections.map((s) => `<h2>${s}</h2><p></p>`).join("");
}

// Extract TipTap headings from editor state for the navigator
function extractEditorHeadings(editor: import("@tiptap/react").Editor): { text: string; level: number; pos: number }[] {
  const headings: { text: string; level: number; pos: number }[] = [];
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === "heading") {
      headings.push({ text: node.textContent, level: node.attrs.level as number, pos });
    }
  });
  return headings;
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="w-8 h-8 rounded-full border-2 border-white/10 border-t-[#7B1A1A] animate-spin" />
    </div>
  );
}

function ErrorMsg({ message }: { message: string }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
      <AlertCircle size={16} />
      {message}
    </div>
  );
}

// ─── Badge Components ─────────────────────────────────────────────────────────

function CategoryBadge({ category }: { category: string }) {
  const map: Record<string, string> = {
    policy:           "bg-blue-500/15 text-blue-400 border-blue-500/30",
    procedure:        "bg-violet-500/15 text-violet-400 border-violet-500/30",
    plan:             "bg-sky-500/15 text-sky-400 border-sky-500/30",
    isms:             "bg-[#7B1A1A]/20 text-[#9B4A4A] border-[#7B1A1A]/30",
    bcp:              "bg-amber-500/15 text-amber-400 border-amber-500/30",
    audit:            "bg-purple-500/15 text-purple-400 border-purple-500/30",
    training:         "bg-green-500/15 text-green-400 border-green-500/30",
    guide:            "bg-teal-500/15 text-teal-400 border-teal-500/30",
    register:         "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    log:              "bg-slate-500/15 text-slate-400 border-slate-500/30",
    minutes:          "bg-indigo-500/15 text-indigo-400 border-indigo-500/30",
    work_instruction: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30",
    context:          "bg-orange-500/15 text-orange-400 border-orange-500/30",
  };
  const labels: Record<string, string> = { work_instruction: "Work Instr." };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${map[category] ?? "bg-white/8 text-white/40 border-white/15"}`}>
      {labels[category] ?? cap(category)}
    </span>
  );
}

function DocStatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    draft:      "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
    review:     "bg-blue-500/15 text-blue-400 border-blue-500/30",
    approved:   "bg-green-500/15 text-green-400 border-green-500/30",
    superseded: "bg-white/8 text-white/40 border-white/15",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${map[status] ?? "bg-white/8 text-white/40 border-white/15"}`}>
      {cap(status)}
    </span>
  );
}

function ControlStatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    "not-started":   "bg-white/8 text-white/40 border-white/15",
    "in-progress":   "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
    "implemented":   "bg-green-500/15 text-green-400 border-green-500/30",
    "not-applicable":"bg-slate-500/15 text-slate-400 border-slate-500/30",
  };
  const labels: Record<string, string> = {
    "not-started": "Not Started", "in-progress": "In Progress",
    "implemented": "Implemented", "not-applicable": "Not Applicable",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${map[status] ?? "bg-white/8 text-white/40 border-white/15"}`}>
      {labels[status] ?? cap(status)}
    </span>
  );
}

function RiskScoreBadge({ score }: { score: number }) {
  let cls = "bg-green-500/15 text-green-400 border-green-500/30";
  if (score >= 6 && score <= 12) cls = "bg-yellow-500/15 text-yellow-400 border-yellow-500/30";
  if (score >= 15) cls = "bg-red-500/15 text-red-400 border-red-500/30";
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${cls}`}>{score}</span>
  );
}

function SeverityBadge({ severity }: { severity: string }) {
  const map: Record<string, string> = {
    critical: "bg-red-500/20 text-red-400 border-red-500/30",
    high:     "bg-orange-500/15 text-orange-400 border-orange-500/30",
    medium:   "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
    low:      "bg-green-500/15 text-green-400 border-green-500/30",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${map[severity] ?? "bg-white/8 text-white/40 border-white/15"}`}>
      {cap(severity)}
    </span>
  );
}

function IncidentStatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    open:          "bg-red-500/15 text-red-400 border-red-500/30",
    investigating: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
    resolved:      "bg-blue-500/15 text-blue-400 border-blue-500/30",
    closed:        "bg-green-500/15 text-green-400 border-green-500/30",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${map[status] ?? "bg-white/8 text-white/40 border-white/15"}`}>
      {cap(status)}
    </span>
  );
}

function AuditStatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    planned:     "bg-blue-500/15 text-blue-400 border-blue-500/30",
    "in-progress":"bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
    complete:    "bg-green-500/15 text-green-400 border-green-500/30",
  };
  const labels: Record<string, string> = {
    planned: "Planned", "in-progress": "In Progress", complete: "Complete",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${map[status] ?? "bg-white/8 text-white/40 border-white/15"}`}>
      {labels[status] ?? cap(status)}
    </span>
  );
}

function FindingSeverityBadge({ severity }: { severity: string }) {
  const map: Record<string, string> = {
    major:       "bg-red-500/20 text-red-400 border-red-500/30",
    minor:       "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
    observation: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${map[severity] ?? "bg-white/8 text-white/40 border-white/15"}`}>
      {cap(severity)}
    </span>
  );
}

function CorrectiveActionBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    "open":        "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
    "in-progress": "bg-blue-500/15 text-blue-400 border-blue-500/30",
    "closed":      "bg-green-500/15 text-green-400 border-green-500/30",
  };
  const labels: Record<string, string> = {
    "open": "Open", "in-progress": "In Progress", "closed": "Closed",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${map[status] ?? "bg-white/8 text-white/40 border-white/15"}`}>
      {labels[status] ?? cap(status)}
    </span>
  );
}

function RAGChip({ rag }: { rag: "Red" | "Amber" | "Green" }) {
  const map = {
    Red:   "bg-red-500/20 text-red-400 border-red-500/30",
    Amber: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
    Green: "bg-green-500/15 text-green-400 border-green-500/30",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${map[rag]}`}>{rag}</span>
  );
}

function ReviewDateCell({ reviewDate }: { reviewDate: string | null }) {
  if (!reviewDate) return <span className="text-white/30 text-xs">—</span>;
  const daysUntil = Math.ceil((new Date(reviewDate).getTime() - Date.now()) / 86400000);
  if (daysUntil < 0) {
    return (
      <span className="flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
        <span className="text-red-400 text-xs">{fmtDate(reviewDate)}</span>
      </span>
    );
  }
  if (daysUntil <= 30) {
    return (
      <span className="flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 flex-shrink-0" />
        <span className="text-yellow-400 text-xs">{fmtDate(reviewDate)}</span>
      </span>
    );
  }
  return <span className="text-white/60 text-xs">{fmtDate(reviewDate)}</span>;
}

// ─── Slide-in Panel ───────────────────────────────────────────────────────────

function SlidePanel({
  open, onClose, title, children,
}: {
  open: boolean; onClose: () => void; title: string; children: React.ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200] flex justify-end">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative flex flex-col w-full sm:w-[420px] h-full bg-[#0D1520] border-l border-white/10 shadow-2xl overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/8 sticky top-0 bg-[#0D1520] z-10">
          <h3 className="text-sm font-semibold text-white">{title}</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
            <XCircle size={18} />
          </button>
        </div>
        <div className="flex-1 px-5 py-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

// ─── Traceability Panel ───────────────────────────────────────────────────────

function TraceabilityPanel({
  doc,
  open,
  onClose,
}: {
  doc: ISODocument;
  open: boolean;
  onClose: () => void;
}) {
  const [links,      setLinks]      = useState<{ risks: ISOLinkedRisk[]; audits: ISOLinkedAudit[] } | null>(null);
  const [allRisks,   setAllRisks]   = useState<ISORisk[]>([]);
  const [loading,    setLoading]    = useState(false);
  const [addRisk,    setAddRisk]    = useState("");
  const [linking,    setLinking]    = useState(false);
  const [unlinking,  setUnlinking]  = useState<string | null>(null);

  const loadLinks = useCallback(async () => {
    setLoading(true);
    try {
      const [lRes, rRes] = await Promise.all([
        fetch(`/api/iso/documents/${doc.id}/links`, { credentials: "include" }),
        fetch("/api/iso/risks", { credentials: "include" }),
      ]);
      if (lRes.ok) { const d = await lRes.json(); setLinks(d.data); }
      if (rRes.ok) { const d = await rRes.json(); setAllRisks(d.data ?? []); }
    } finally {
      setLoading(false);
    }
  }, [doc.id]);

  useEffect(() => { if (open) loadLinks(); }, [open, loadLinks]);

  async function handleLinkRisk() {
    if (!addRisk) return;
    setLinking(true);
    try {
      await fetch(`/api/iso/documents/${doc.id}/risks`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ risk_id: addRisk }),
      });
      setAddRisk("");
      await loadLinks();
    } finally { setLinking(false); }
  }

  async function handleUnlinkRisk(riskId: string) {
    setUnlinking(riskId);
    try {
      await fetch(`/api/iso/documents/${doc.id}/risks/${riskId}`, {
        method: "DELETE", credentials: "include",
      });
      await loadLinks();
    } finally { setUnlinking(null); }
  }

  // Risks not yet linked
  const linkedIds = new Set(links?.risks.map((r) => r.id) ?? []);
  const available = allRisks.filter((r) => !linkedIds.has(r.id));

  return (
    <SlidePanel open={open} onClose={onClose} title="Traceability">
      {/* Document chip */}
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/4 border border-white/8 -mt-2">
        <GitBranch size={13} className="text-[#9B4A4A] flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-xs font-medium text-white/80 truncate">{doc.title}</p>
          <p className="text-xs text-white/35 font-mono">{doc.code}</p>
        </div>
      </div>

      {loading ? (
        <Spinner />
      ) : (
        <>
          {/* ── Linked Risks ── */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <AlertTriangle size={13} className="text-yellow-400" />
              <span className="text-xs font-semibold text-white/70 uppercase tracking-wide">Linked Risks</span>
              {links?.risks.length ? (
                <span className="text-xs text-white/35">{links.risks.length}</span>
              ) : null}
            </div>

            {links?.risks.length === 0 && (
              <p className="text-xs text-white/30 italic py-1">No risks linked.</p>
            )}

            <div className="space-y-1.5">
              {links?.risks.map((risk) => (
                <div key={risk.id} className="flex items-start gap-2 px-3 py-2 rounded-lg bg-white/4 border border-white/8 group">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="font-mono text-xs text-white/40">{risk.riskId}</span>
                      <RiskScoreBadge score={risk.score} />
                    </div>
                    <p className="text-xs text-white/70 truncate">{risk.threat}</p>
                    <p className="text-xs text-white/40 truncate">{risk.asset}</p>
                  </div>
                  <button
                    onClick={() => handleUnlinkRisk(risk.id)}
                    disabled={unlinking === risk.id}
                    className="p-1 text-white/20 hover:text-red-400 transition-colors disabled:opacity-40 flex-shrink-0 opacity-0 group-hover:opacity-100"
                    title="Unlink risk"
                  >
                    {unlinking === risk.id
                      ? <div className="w-3 h-3 rounded-full border border-white/30 border-t-red-400 animate-spin" />
                      : <Unlink size={11} />}
                  </button>
                </div>
              ))}
            </div>

            {/* Add risk link */}
            {available.length > 0 && (
              <div className="flex gap-2">
                <select
                  className={`${selectCls} flex-1 text-xs`}
                  value={addRisk}
                  onChange={(e) => setAddRisk(e.target.value)}
                >
                  <option value="">— link a risk —</option>
                  {available.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.riskId} · {r.threat.slice(0, 40)}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleLinkRisk}
                  disabled={!addRisk || linking}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#7B1A1A] hover:bg-[#9B2A2A] disabled:opacity-40 text-white text-xs transition-colors flex-shrink-0"
                >
                  <Link2 size={11} /> Link
                </button>
              </div>
            )}
          </div>

          {/* ── Related Audits ── */}
          <div className="space-y-2 pt-1 border-t border-white/8">
            <div className="flex items-center gap-2">
              <ClipboardList size={13} className="text-blue-400" />
              <span className="text-xs font-semibold text-white/70 uppercase tracking-wide">Related Audits</span>
              {links?.audits.length ? (
                <span className="text-xs text-white/35">{links.audits.length}</span>
              ) : null}
            </div>

            {links?.audits.length === 0 && (
              <p className="text-xs text-white/30 italic py-1">No audit findings reference this document.</p>
            )}

            <div className="space-y-1.5">
              {links?.audits.map((audit) => (
                <div key={audit.id} className="px-3 py-2 rounded-lg bg-white/4 border border-white/8">
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <p className="text-xs font-medium text-white/80 truncate">{audit.title}</p>
                    <AuditStatusBadge status={audit.status} />
                  </div>
                  <div className="flex items-center gap-3 text-xs text-white/40">
                    <span>{cap(audit.type)}</span>
                    {audit.plannedDate && <span>{fmtDate(audit.plannedDate)}</span>}
                    <span className="text-purple-400">{audit.findingCount} finding{audit.findingCount !== 1 ? "s" : ""}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </SlidePanel>
  );
}

// ─── Form Field Helpers ───────────────────────────────────────────────────────

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-medium text-white/60">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

const inputCls    = "w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#7B1A1A]/60 focus:ring-1 focus:ring-[#7B1A1A]/30";
const selectCls   = "w-full bg-[#101828] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#7B1A1A]/60 focus:ring-1 focus:ring-[#7B1A1A]/30";
const textareaCls = "w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#7B1A1A]/60 focus:ring-1 focus:ring-[#7B1A1A]/30 resize-none";

// ─── Progress Bar ─────────────────────────────────────────────────────────────

function ProgressBar({ label, value, total }: { label: string; value: number; total: number }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between text-xs">
        <span className="text-white/70">{label}</span>
        <span className="text-white/50">{value}/{total} ({pct}%)</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/8 overflow-hidden">
        <div className="h-full rounded-full bg-[#7B1A1A] transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ─── TAB 1: Dashboard ─────────────────────────────────────────────────────────

function DashboardTab() {
  const [dashboard,    setDashboard]    = useState<ISODashboard | null>(null);
  const [controls,     setControls]     = useState<ISOControl[]>([]);
  const [reviewAlerts, setReviewAlerts] = useState<{ overdue: ISOReviewAlert[]; dueSoon: ISOReviewAlert[] } | null>(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [dRes, cRes, rRes] = await Promise.all([
        fetch("/api/iso/dashboard",      { credentials: "include" }),
        fetch("/api/iso/controls",       { credentials: "include" }),
        fetch("/api/iso/review-alerts",  { credentials: "include" }),
      ]);
      if (!dRes.ok) throw new Error("Failed to load dashboard");
      if (!cRes.ok) throw new Error("Failed to load controls");
      const dData = await dRes.json();
      const cData = await cRes.json();
      setDashboard(dData.data);
      setControls(cData.data ?? []);
      if (rRes.ok) {
        const rData = await rRes.json();
        setReviewAlerts(rData.data ?? null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Load failed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Spinner />;
  if (error)   return <ErrorMsg message={error} />;
  if (!dashboard) return null;

  const cats = ["Organizational", "People", "Physical", "Technological"] as const;
  const catCounts: Record<string, { total: number; implemented: number }> = {};
  for (const cat of cats) {
    const group = controls.filter((c) => c.category === cat && c.applicable);
    catCounts[cat] = {
      total:       group.length,
      implemented: group.filter((c) => c.status === "implemented").length,
    };
  }

  type RAGValue = "Red" | "Amber" | "Green";
  const d = dashboard; // narrowed — null checked above
  function rag(area: string): RAGValue {
    switch (area) {
      case "Documents": {
        const pct = d.documents.total > 0 ? d.documents.approved / d.documents.total : 0;
        return pct >= 0.8 ? "Green" : pct >= 0.5 ? "Amber" : "Red";
      }
      case "Risk Management":
        return d.risks.open === 0 ? "Green" : d.risks.open <= 5 ? "Amber" : "Red";
      case "Controls": {
        const pct = d.controls.total > 0 ? d.controls.implemented / d.controls.total : 0;
        return pct >= 0.8 ? "Green" : pct >= 0.5 ? "Amber" : "Red";
      }
      case "Incident Management":
        return d.incidents.open === 0 ? "Green" : d.incidents.open <= 3 ? "Amber" : "Red";
      default:
        return "Amber";
    }
  }

  const ismsAreas = ["Documents", "Risk Management", "Asset Management", "Controls", "Incident Management", "Business Continuity"];

  const hasReviewAlerts = (reviewAlerts?.overdue.length ?? 0) > 0 || (reviewAlerts?.dueSoon.length ?? 0) > 0;

  return (
    <div className="space-y-6">

      {/* Review alerts detailed panel */}
      {hasReviewAlerts && reviewAlerts && (
        <div className="bg-white/4 border border-white/8 rounded-xl overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-3.5 border-b border-white/8">
            <AlertTriangle size={15} className="text-red-400 flex-shrink-0" />
            <span className="text-sm font-semibold text-white">Document Review Alerts</span>
            <div className="ml-auto flex items-center gap-2">
              {reviewAlerts.overdue.length > 0 && (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-500/15 text-red-400 border border-red-500/25">
                  {reviewAlerts.overdue.length} overdue
                </span>
              )}
              {reviewAlerts.dueSoon.length > 0 && (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-500/15 text-yellow-400 border border-yellow-500/25">
                  {reviewAlerts.dueSoon.length} due soon
                </span>
              )}
            </div>
          </div>

          {reviewAlerts.overdue.length > 0 && (
            <>
              <div className="px-5 py-2 bg-red-500/5 border-b border-white/5">
                <span className="text-xs font-semibold text-red-400 uppercase tracking-wide">Overdue</span>
              </div>
              <div className="divide-y divide-white/5">
                {reviewAlerts.overdue.map((doc) => {
                  const daysOverdue = Math.ceil((Date.now() - new Date(doc.reviewDate).getTime()) / 86400000);
                  return (
                    <div key={doc.id} className="flex items-center gap-3 px-5 py-2.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-white/85 truncate">{doc.title}</p>
                        <p className="text-xs text-white/35 font-mono">{doc.code}</p>
                      </div>
                      <CategoryBadge category={doc.category} />
                      <span className="text-xs text-red-400 whitespace-nowrap">{daysOverdue}d overdue</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {reviewAlerts.dueSoon.length > 0 && (
            <>
              <div className={`px-5 py-2 bg-yellow-500/5 border-b border-white/5 ${reviewAlerts.overdue.length > 0 ? "border-t border-white/8 mt-0" : ""}`}>
                <span className="text-xs font-semibold text-yellow-400 uppercase tracking-wide">Due within 30 days</span>
              </div>
              <div className="divide-y divide-white/5">
                {reviewAlerts.dueSoon.map((doc) => {
                  const daysUntil = Math.ceil((new Date(doc.reviewDate).getTime() - Date.now()) / 86400000);
                  return (
                    <div key={doc.id} className="flex items-center gap-3 px-5 py-2.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-white/85 truncate">{doc.title}</p>
                        <p className="text-xs text-white/35 font-mono">{doc.code}</p>
                      </div>
                      <CategoryBadge category={doc.category} />
                      <span className="text-xs text-yellow-400 whitespace-nowrap">{daysUntil}d left</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard
          label="Documents Approved"
          value={`${dashboard.documents.approved}/${dashboard.documents.total}`}
          icon={<FileText size={18} className="text-[#7B1A1A]" />}
        />
        <StatCard
          label="Controls Implemented"
          value={`${dashboard.controls.implemented}/${dashboard.controls.total}`}
          icon={<CheckCircle2 size={18} className="text-green-400" />}
        />
        <StatCard
          label="Open Risks"
          value={String(dashboard.risks.open)}
          icon={<AlertTriangle size={18} className="text-yellow-400" />}
          highlight={dashboard.risks.open > 5}
        />
        <StatCard
          label="Open Incidents"
          value={String(dashboard.incidents.open)}
          icon={<AlertCircle size={18} className="text-orange-400" />}
          highlight={dashboard.incidents.open > 0}
        />
        <StatCard
          label="Audits Planned"
          value={String(dashboard.audits.planned)}
          icon={<ClipboardList size={18} className="text-blue-400" />}
        />
      </div>

      {/* Compliance progress */}
      <div className="bg-white/4 border border-white/8 rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <BarChart2 size={16} className="text-[#7B1A1A]" />
          <h3 className="text-sm font-semibold text-white">Compliance Progress by Control Category</h3>
        </div>
        <div className="space-y-3">
          {cats.map((cat) => (
            <ProgressBar key={cat} label={cat} value={catCounts[cat].implemented} total={catCounts[cat].total} />
          ))}
        </div>
      </div>

      {/* ISMS quick-status table */}
      <div className="bg-white/4 border border-white/8 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-white/8 flex items-center gap-2">
          <ShieldCheck size={16} className="text-[#7B1A1A]" />
          <h3 className="text-sm font-semibold text-white">ISMS Area Status</h3>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/8">
              <th className="px-5 py-2.5 text-left text-xs font-medium text-white/50 uppercase tracking-wide">Area</th>
              <th className="px-5 py-2.5 text-left text-xs font-medium text-white/50 uppercase tracking-wide">RAG Status</th>
            </tr>
          </thead>
          <tbody>
            {ismsAreas.map((area, i) => (
              <tr key={area} className={i < ismsAreas.length - 1 ? "border-b border-white/5" : ""}>
                <td className="px-5 py-3 text-white/80">{area}</td>
                <td className="px-5 py-3"><RAGChip rag={rag(area)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon, highlight }: { label: string; value: string; icon: React.ReactNode; highlight?: boolean }) {
  return (
    <div className={`bg-white/4 border rounded-xl p-4 space-y-2 ${highlight ? "border-red-500/30" : "border-white/8"}`}>
      <div className="flex items-center justify-between">{icon}</div>
      <div className={`text-xl font-bold ${highlight ? "text-red-400" : "text-white"}`}>{value}</div>
      <div className="text-xs text-white/50">{label}</div>
    </div>
  );
}

// ─── Document Content Editor Modal ───────────────────────────────────────────

function DocumentEditorModal({
  doc,
  onClose,
  onSaved,
  defaultEditing = false,
}: {
  doc: ISODocument;
  onClose: () => void;
  onSaved: () => void;
  defaultEditing?: boolean;
}) {
  const hasPdf     = !!doc.fileUrl;
  const parsedSections = parseSectionedContent(doc.content);
  const isSectioned = !!parsedSections;

  const [viewMode, setViewMode] = useState<"pdf" | "text">(
    hasPdf && !defaultEditing ? "pdf" : "text"
  );
  const [editing,   setEditing]   = useState(defaultEditing);
  const [saving,    setSaving]    = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [navOpen,   setNavOpen]   = useState(false);
  const originalHtml = useRef<string>("");

  // For sectioned docs: state per section, for flat docs: single TipTap
  const [sections, setSections] = useState<Record<string, string>>(parsedSections ?? {});
  const [activeSection, setActiveSection] = useState<string>(
    parsedSections ? Object.keys(parsedSections)[0] ?? "" : ""
  );

  // Single editor for flat (non-sectioned) content
  const initialHtml = isSectioned ? "" : plainTextToHtml(doc.content);
  const editor = useEditor({
    extensions: [StarterKit],
    content: initialHtml,
    editable: false,
  });

  // Section editor (one instance, content swaps per active section)
  const sectionEditor = useEditor({
    extensions: [StarterKit],
    content: parsedSections?.[activeSection] ?? "",
    editable: false,
  });

  useEffect(() => {
    originalHtml.current = initialHtml;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(editing);
    if (editing && !isSectioned) setTimeout(() => editor.commands.focus(), 50);
  }, [editor, editing, isSectioned]);

  useEffect(() => {
    if (!sectionEditor) return;
    sectionEditor.setEditable(editing);
    if (editing && isSectioned) setTimeout(() => sectionEditor.commands.focus(), 50);
  }, [sectionEditor, editing, isSectioned]);

  // Swap section editor content when active section changes
  useEffect(() => {
    if (!sectionEditor || !isSectioned) return;
    sectionEditor.commands.setContent(sections[activeSection] ?? "");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  useEffect(() => {
    if (editing) return;
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose, editing]);

  function handleEdit() {
    if (!isSectioned) originalHtml.current = editor?.getHTML() ?? initialHtml;
    setEditing(true);
    setViewMode("text");
    setSaveError(null);
  }

  function handleCancel() {
    if (isSectioned) {
      setSections(parsedSections ?? {});
      sectionEditor?.commands.setContent(parsedSections?.[activeSection] ?? "");
    } else {
      editor?.commands.setContent(originalHtml.current);
    }
    setEditing(false);
    if (hasPdf) setViewMode("pdf");
    setSaveError(null);
  }

  function handleSectionSwitch(sec: string) {
    // Save current section content before switching
    if (editing && isSectioned && sectionEditor) {
      setSections((prev) => ({ ...prev, [activeSection]: sectionEditor.getHTML() }));
    }
    setActiveSection(sec);
  }

  function applyTemplate() {
    const tpl = SECTION_TEMPLATES[doc.category] ?? SECTION_TEMPLATES.policy;
    const html = buildTemplateHtml(tpl);
    editor?.commands.setContent(html);
    setEditing(true);
    setViewMode("text");
  }

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    try {
      let finalContent: string;
      if (isSectioned && sectionEditor) {
        const updated = { ...sections, [activeSection]: sectionEditor.getHTML() };
        finalContent = buildSectionedContent(updated);
      } else {
        if (!editor) throw new Error("Editor not ready");
        finalContent = editor.getHTML();
      }
      const res = await fetch(`/api/iso/documents/${doc.id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: finalContent }),
      });
      if (!res.ok) throw new Error("Save failed");
      setEditing(false);
      onSaved();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  // Toolbar button helper
  function TB({
    title, active, fn, children,
  }: { title: string; active?: boolean; fn: () => void; children: React.ReactNode }) {
    return (
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); fn(); }}
        title={title}
        className={`px-2 py-1 rounded text-xs transition-colors select-none ${
          active ? "bg-white/15 text-white" : "text-white/50 hover:text-white hover:bg-white/8"
        }`}
      >
        {children}
      </button>
    );
  }

  const editorAreaCls = `
    [&_.ProseMirror]:outline-none
    [&_.ProseMirror]:min-h-[400px]
    [&_.ProseMirror_p]:text-white/85
    [&_.ProseMirror_p]:text-sm
    [&_.ProseMirror_p]:leading-7
    [&_.ProseMirror_p]:mb-4
    [&_.ProseMirror_h1]:text-2xl
    [&_.ProseMirror_h1]:font-bold
    [&_.ProseMirror_h1]:text-white
    [&_.ProseMirror_h1]:mb-5
    [&_.ProseMirror_h1]:mt-8
    [&_.ProseMirror_h1]:pb-2
    [&_.ProseMirror_h1]:border-b
    [&_.ProseMirror_h1]:border-white/10
    [&_.ProseMirror_h2]:text-xl
    [&_.ProseMirror_h2]:font-semibold
    [&_.ProseMirror_h2]:text-white
    [&_.ProseMirror_h2]:mb-4
    [&_.ProseMirror_h2]:mt-6
    [&_.ProseMirror_h3]:text-base
    [&_.ProseMirror_h3]:font-semibold
    [&_.ProseMirror_h3]:text-white/90
    [&_.ProseMirror_h3]:mb-3
    [&_.ProseMirror_h3]:mt-5
    [&_.ProseMirror_ul]:list-disc
    [&_.ProseMirror_ul]:pl-6
    [&_.ProseMirror_ul]:mb-4
    [&_.ProseMirror_ul]:text-white/80
    [&_.ProseMirror_ul]:text-sm
    [&_.ProseMirror_ol]:list-decimal
    [&_.ProseMirror_ol]:pl-6
    [&_.ProseMirror_ol]:mb-4
    [&_.ProseMirror_ol]:text-white/80
    [&_.ProseMirror_ol]:text-sm
    [&_.ProseMirror_li]:mb-1
    [&_.ProseMirror_blockquote]:border-l-4
    [&_.ProseMirror_blockquote]:border-[#7B1A1A]/60
    [&_.ProseMirror_blockquote]:pl-4
    [&_.ProseMirror_blockquote]:text-white/60
    [&_.ProseMirror_blockquote]:italic
    [&_.ProseMirror_blockquote]:mb-4
    [&_.ProseMirror_strong]:font-semibold
    [&_.ProseMirror_strong]:text-white
    [&_.ProseMirror_em]:italic
    [&_.ProseMirror_code]:bg-white/8
    [&_.ProseMirror_code]:rounded
    [&_.ProseMirror_code]:px-1.5
    [&_.ProseMirror_code]:py-0.5
    [&_.ProseMirror_code]:font-mono
    [&_.ProseMirror_code]:text-xs
    [&_.ProseMirror_code]:text-white/80
    [&_.ProseMirror_pre]:bg-white/5
    [&_.ProseMirror_pre]:border
    [&_.ProseMirror_pre]:border-white/8
    [&_.ProseMirror_pre]:rounded-xl
    [&_.ProseMirror_pre]:p-4
    [&_.ProseMirror_pre]:mb-4
    [&_.ProseMirror_pre]:overflow-x-auto
    [&_.ProseMirror_pre_code]:bg-transparent
    [&_.ProseMirror_pre_code]:p-0
    [&_.ProseMirror_pre_code]:text-white/70
  `;

  return (
    <div className="fixed inset-0 z-[250] flex flex-col bg-[#0D1520]">

      {/* ── Header ── */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/8 flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors flex-shrink-0" title="Close">
            <ArrowLeft size={16} />
          </button>
          <div className="h-4 w-px bg-white/15 flex-shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs text-white/50">{doc.code}</span>
              <DocStatusBadge status={doc.status} />
              {doc.version && <span className="text-xs text-white/30">v{doc.version}</span>}
              {hasPdf && !editing && (
                <span className="text-xs text-green-400/70">· PDF attached</span>
              )}
            </div>
            <h2 className="text-sm font-semibold text-white truncate">{doc.title}</h2>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0 ml-4">
          {/* Export PDF */}
          {!editing && (
            <a
              href={`/api/iso/documents/${doc.id}/export-pdf`}
              download
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/55 hover:text-white text-xs transition-colors"
              title="Export as formatted PDF"
            >
              <Download size={12} />
              Export PDF
            </a>
          )}

          {/* PDF / Content toggle (view mode only, when file is attached) */}
          {hasPdf && !editing && (
            <div className="flex items-center rounded-lg border border-white/10 overflow-hidden">
              <button
                onClick={() => setViewMode("pdf")}
                className={`px-3 py-1.5 text-xs transition-colors ${viewMode === "pdf" ? "bg-white/10 text-white" : "text-white/45 hover:text-white hover:bg-white/5"}`}
              >
                PDF
              </button>
              <div className="w-px h-4 bg-white/10" />
              <button
                onClick={() => setViewMode("text")}
                className={`px-3 py-1.5 text-xs transition-colors ${viewMode === "text" ? "bg-white/10 text-white" : "text-white/45 hover:text-white hover:bg-white/5"}`}
              >
                Content
              </button>
            </div>
          )}

          {editing ? (
            <>
              <button
                onClick={handleCancel}
                className="px-3 py-1.5 rounded-lg border border-white/10 text-white/60 hover:text-white text-xs transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-1.5 rounded-lg bg-[#7B1A1A] hover:bg-[#9B2A2A] disabled:opacity-50 text-white text-xs font-medium transition-colors"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </>
          ) : (
            <button
              onClick={handleEdit}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 hover:text-white text-xs transition-colors"
            >
              <Edit2 size={12} />
              Edit Content
            </button>
          )}
        </div>
      </div>

      {/* ── Toolbar (edit mode only) ── */}
      {editing && (
        <div className="flex items-center gap-0.5 px-4 py-2 border-b border-white/8 flex-shrink-0 flex-wrap">
          {(isSectioned ? [sectionEditor] : [editor]).map((ed) => ed && (
            <div key="toolbar" className="flex items-center gap-0.5 flex-wrap">
              <TB title="Bold"   active={ed.isActive("bold")}   fn={() => ed.chain().focus().toggleBold().run()}><strong>B</strong></TB>
              <TB title="Italic" active={ed.isActive("italic")} fn={() => ed.chain().focus().toggleItalic().run()}><em>I</em></TB>
              <TB title="Strike" active={ed.isActive("strike")} fn={() => ed.chain().focus().toggleStrike().run()}><span className="line-through">S</span></TB>
              <div className="w-px h-4 bg-white/10 mx-1 flex-shrink-0" />
              {([1, 2, 3] as const).map((lvl) => (
                <TB key={lvl} title={`H${lvl}`} active={ed.isActive("heading", { level: lvl })} fn={() => ed.chain().focus().toggleHeading({ level: lvl }).run()}>H{lvl}</TB>
              ))}
              <div className="w-px h-4 bg-white/10 mx-1 flex-shrink-0" />
              <TB title="Bullets"  active={ed.isActive("bulletList")}  fn={() => ed.chain().focus().toggleBulletList().run()}>• List</TB>
              <TB title="Numbered" active={ed.isActive("orderedList")} fn={() => ed.chain().focus().toggleOrderedList().run()}>1. List</TB>
              <div className="w-px h-4 bg-white/10 mx-1 flex-shrink-0" />
              <TB title="Quote"  active={ed.isActive("blockquote")} fn={() => ed.chain().focus().toggleBlockquote().run()}>❝</TB>
              <div className="w-px h-4 bg-white/10 mx-1 flex-shrink-0" />
              <TB title="Undo" fn={() => ed.chain().focus().undo().run()}>↩</TB>
              <TB title="Redo" fn={() => ed.chain().focus().redo().run()}>↪</TB>
            </div>
          ))}
          {!isSectioned && editing && (
            <button onClick={applyTemplate} className="ml-auto text-xs text-white/40 hover:text-white px-2 py-1 rounded hover:bg-white/5 transition-colors">
              Use Template
            </button>
          )}
        </div>
      )}

      {/* ── Save error ── */}
      {saveError && (
        <div className="px-6 pt-3 flex-shrink-0">
          <ErrorMsg message={saveError} />
        </div>
      )}

      {/* ── Content area ── */}
      {viewMode === "pdf" && hasPdf && !editing ? (
        /* PDF viewer — fills remaining height */
        <div className="flex-1 flex flex-col overflow-hidden">
          <iframe
            src={`/api/iso/documents/${doc.id}/file`}
            title={doc.title}
            className="flex-1 w-full border-0 bg-[#1A1A2E]"
          />
        </div>
      ) : isSectioned ? (
        /* Sectioned layout: left nav + per-section editor */
        <div className="flex-1 flex overflow-hidden">
          {/* Section nav */}
          <div className="w-44 flex-shrink-0 border-r border-white/8 overflow-y-auto bg-[#0D1520]">
            <div className="px-3 py-3 space-y-0.5">
              {Object.keys(sections).map((sec) => (
                <button
                  key={sec}
                  onClick={() => handleSectionSwitch(sec)}
                  className={`w-full text-left px-2.5 py-1.5 rounded-md text-xs transition-colors ${
                    activeSection === sec
                      ? "bg-[#7B1A1A]/30 text-white border-l-2 border-[#7B1A1A]"
                      : "text-white/50 hover:text-white hover:bg-white/5"
                  }`}
                >
                  {sec}
                </button>
              ))}
            </div>
          </div>
          {/* Section content */}
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-3xl mx-auto px-8 py-8">
              <h2 className="text-lg font-semibold text-white mb-6 pb-3 border-b border-white/8">{activeSection}</h2>
              <div className={`${editorAreaCls} ${editing ? "border border-white/10 rounded-xl p-6" : ""}`}>
                <EditorContent editor={sectionEditor} />
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Flat (non-sectioned) content */
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-8 py-8">
            {!doc.content && !editing ? (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <FileText size={40} className="text-white/15 mb-4" />
                <p className="text-white/40 text-sm mb-4">No text content for this document yet.</p>
                {hasPdf && (
                  <p className="text-white/30 text-xs mb-6">A PDF is attached — switch to PDF view to read it.</p>
                )}
                <button
                  onClick={handleEdit}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 hover:text-white text-sm transition-colors"
                >
                  <Edit2 size={14} /> Add content
                </button>
              </div>
            ) : (
              <div className={`${editorAreaCls} ${editing ? "border border-white/10 rounded-xl p-6" : ""}`}>
                <EditorContent editor={editor} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Import Document Modal ────────────────────────────────────────────────────

function ImportDocumentModal({
  onClose,
  onImported,
}: {
  onClose: () => void;
  onImported: (docId: string) => void;
}) {
  const [step,      setStep]      = useState<"upload" | "review">("upload");
  const [dragging,  setDragging]  = useState(false);
  const [file,      setFile]      = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [error,     setError]     = useState<string | null>(null);
  const [preview,   setPreview]   = useState<{ code: string; title: string; category: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);

  function handleFilePick(f: File) {
    const allowed = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (!allowed.includes(f.type)) { setError("Only PDF and Word (.docx, .doc) files are supported."); return; }
    if (f.size > 50 * 1024 * 1024)  { setError("File must be under 50 MB."); return; }
    setError(null);
    setFile(f);
    const name = f.name.replace(/\.[^.]+$/, "");
    const m = name.match(/^ISMS-([A-Z]+)-(\d+)\s+(.+)$/);
    const TYPE_MAP: Record<string, string> = {
      PL: "policy", PR: "procedure", REG: "register", LOG: "log",
      PLN: "plan", AUD: "audit", MIN: "minutes", WI: "work_instruction",
      DOC: "context", BCP: "bcp",
    };
    if (m) {
      setPreview({ code: `ISMS-${m[1]}-${m[2]}`, title: m[3], category: TYPE_MAP[m[1]] ?? "context" });
    } else {
      setPreview({ code: name.slice(0, 20), title: name, category: "context" });
    }
    setStep("review");
  }

  async function handleImport() {
    if (!file) return;
    setImporting(true); setError(null);
    try {
      const res  = await fetch(
        `/api/iso/import?filename=${encodeURIComponent(file.name)}`,
        { method: "POST", credentials: "include", headers: { "Content-Type": file.type || "application/octet-stream" }, body: file },
      );
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 409 && data.existingId) { onImported(data.existingId); return; }
        throw new Error(data.message || "Import failed");
      }
      onImported(data.data?.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Import failed");
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[#0D1520] border border-white/10 rounded-2xl shadow-2xl p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FilePlus2 size={16} className="text-[#9B4A4A]" />
            <h3 className="text-sm font-semibold text-white">Import Document</h3>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors"><XCircle size={16} /></button>
        </div>

        {step === "upload" ? (
          <>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) handleFilePick(f); }}
              onClick={() => fileInputRef.current?.click()}
              className={`flex flex-col items-center justify-center gap-3 border-2 border-dashed rounded-xl py-12 cursor-pointer transition-colors ${
                dragging ? "border-[#7B1A1A]/60 bg-[#7B1A1A]/10" : "border-white/15 hover:border-white/30 hover:bg-white/3"
              }`}
            >
              <Upload size={28} className="text-white/30" />
              <div className="text-center">
                <p className="text-sm text-white/60">Drop a Word or PDF file here</p>
                <p className="text-xs text-white/35 mt-1">or click to browse · max 50 MB</p>
              </div>
              <p className="text-xs text-white/20 font-mono">ISMS-PL-001 Information Security Policy.docx</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFilePick(f); }}
            />
            {error && <ErrorMsg message={error} />}
          </>
        ) : (
          <>
            <div className="space-y-3">
              <p className="text-xs text-white/50">Review the auto-detected details before importing:</p>
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <FileText size={14} className="text-white/40 flex-shrink-0" />
                  <span className="text-sm text-white/80 truncate">{file?.name}</span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <p className="text-white/40 mb-1">Document Code</p>
                    <p className="text-white font-mono">{preview?.code}</p>
                  </div>
                  <div>
                    <p className="text-white/40 mb-1.5">Category</p>
                    <CategoryBadge category={preview?.category ?? "context"} />
                  </div>
                </div>
                <div className="text-xs">
                  <p className="text-white/40 mb-1">Title</p>
                  <p className="text-white/90">{preview?.title}</p>
                </div>
              </div>
              <p className="text-xs text-white/30">
                Text content will be extracted and split into sections. The original file is stored as a backup.
              </p>
            </div>
            {error && <ErrorMsg message={error} />}
            <div className="flex gap-2">
              <button
                onClick={() => { setStep("upload"); setFile(null); setPreview(null); setError(null); }}
                className="flex-1 border border-white/10 text-white/50 hover:text-white rounded-lg py-2 text-sm transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleImport}
                disabled={importing}
                className="flex-1 bg-[#7B1A1A] hover:bg-[#9B2A2A] disabled:opacity-50 text-white rounded-lg py-2 text-sm font-medium transition-colors"
              >
                {importing ? "Importing…" : "Import Document"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Master Document Index Modal ──────────────────────────────────────────────

function MasterIndexModal({ onClose }: { onClose: () => void }) {
  const [docs,    setDocs]    = useState<ISODocument[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);

  useEffect(() => {
    fetch("/api/iso/documents", { credentials: "include" })
      .then((r) => r.json())
      .then((d) => setDocs(d.data ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const ordered = [
    "isms", "policy", "procedure", "plan", "bcp",
    "register", "log", "audit", "minutes", "work_instruction", "context", "guide", "training",
  ];
  const catLabel: Record<string, string> = {
    isms: "ISMS Core", bcp: "Business Continuity",
    work_instruction: "Work Instructions", minutes: "Meeting Minutes",
  };
  const grouped: Record<string, ISODocument[]> = {};
  for (const cat of ordered) grouped[cat] = docs.filter((d) => d.category === cat);

  return (
    <div className="fixed inset-0 z-[300] flex flex-col bg-[#0D1520]">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/8 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-lg bg-[#7B1A1A]/20 border border-[#7B1A1A]/30 flex items-center justify-center">
            <TableProperties size={14} className="text-[#9B4A4A]" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-white">Master Document Index</h2>
            <p className="text-xs text-white/40">{docs.length} document{docs.length !== 1 ? "s" : ""} · ISO 27001 ISMS</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="/api/iso/document-index/pdf"
            download
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#7B1A1A] hover:bg-[#9B2A2A] text-white text-xs font-medium transition-colors"
          >
            <Download size={12} />
            Download PDF
          </a>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors ml-2">
            <XCircle size={18} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8">
        {loading ? (
          <Spinner />
        ) : (
          ordered.map((cat) => {
            const group = grouped[cat];
            if (!group || group.length === 0) return null;
            return (
              <div key={cat}>
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="text-xs font-semibold text-white/70 uppercase tracking-wide">
                    {catLabel[cat] ?? cap(cat)}
                  </h3>
                  <span className="text-xs text-white/30">{group.length}</span>
                  <div className="flex-1 h-px bg-white/8" />
                </div>
                <div className="bg-white/4 border border-white/8 rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/8">
                          {["Code", "Title", "Version", "Status", "Owner", "Next Review"].map((h) => (
                            <th key={h} className="px-4 py-2.5 text-left text-xs font-medium text-white/40 uppercase tracking-wide whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {group.map((doc, i) => (
                          <tr key={doc.id} className={i < group.length - 1 ? "border-b border-white/5" : ""}>
                            <td className="px-4 py-2.5 font-mono text-xs text-white/55 whitespace-nowrap">{doc.code}</td>
                            <td className="px-4 py-2.5 text-white/80 max-w-[300px] truncate">{doc.title}</td>
                            <td className="px-4 py-2.5 text-white/50 text-xs whitespace-nowrap">v{doc.version}</td>
                            <td className="px-4 py-2.5 whitespace-nowrap"><DocStatusBadge status={doc.status} /></td>
                            <td className="px-4 py-2.5 text-white/50 text-xs whitespace-nowrap">{doc.owner || "—"}</td>
                            <td className="px-4 py-2.5 whitespace-nowrap"><ReviewDateCell reviewDate={doc.reviewDate} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ─── TAB 2: Documents ─────────────────────────────────────────────────────────

function DocumentsTab() {
  const [docs,      setDocs]      = useState<ISODocument[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState<string | null>(null);
  const [catFilter, setCatFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");

  // Edit panel
  const [editOpen,  setEditOpen]  = useState(false);
  const [editing,   setEditing]   = useState<ISODocument | null>(null);
  const [saving,    setSaving]    = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // History panel
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyDoc,  setHistoryDoc]  = useState<ISODocument | null>(null);
  const [logs,        setLogs]        = useState<ISODocumentLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // Content editor modal
  const [editorDoc,       setEditorDoc]       = useState<ISODocument | null>(null);
  const [editorStartEdit, setEditorStartEdit] = useState(false);

  // Import & index modals
  const [importOpen, setImportOpen] = useState(false);
  const [indexOpen,  setIndexOpen]  = useState(false);

  // Traceability panel
  const [traceDoc,  setTraceDoc]  = useState<ISODocument | null>(null);
  const [traceOpen, setTraceOpen] = useState(false);

  function openView(doc: ISODocument)        { setEditorStartEdit(false); setEditorDoc(doc); }
  function openContentEdit(doc: ISODocument) { setEditorStartEdit(true);  setEditorDoc(doc); }

  // Search
  const [search, setSearch] = useState("");

  // File upload
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadTargetRef = useRef<string | null>(null);

  type DocForm = {
    code: string; title: string; category: string; status: string;
    owner: string; version: string; reviewDate: string; lastReviewed: string;
    notes: string; content: string;
    author: string; reviewer: string; approver: string;
  };
  const emptyForm: DocForm = {
    code: "", title: "", category: "policy", status: "draft",
    owner: "", version: "1.0", reviewDate: "", lastReviewed: "", notes: "", content: "",
    author: "", reviewer: "", approver: "",
  };
  const [form, setForm] = useState<DocForm>(emptyForm);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch("/api/iso/documents", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load documents");
      const data = await res.json();
      setDocs(data.data ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Load failed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function openNew() {
    setEditing(null);
    setForm(emptyForm);
    setFormError(null);
    setEditOpen(true);
  }

  function openEdit(doc: ISODocument) {
    setEditing(doc);
    setForm({
      code:         doc.code,
      title:        doc.title,
      category:     doc.category,
      status:       doc.status,
      owner:        doc.owner ?? "",
      version:      doc.version ?? "1.0",
      reviewDate:   doc.reviewDate ? doc.reviewDate.slice(0, 10) : "",
      lastReviewed: doc.lastReviewed ? doc.lastReviewed.slice(0, 10) : "",
      notes:        doc.notes ?? "",
      content:      doc.content ?? "",
      author:       doc.author ?? "",
      reviewer:     doc.reviewer ?? "",
      approver:     doc.approver ?? "",
    });
    setFormError(null);
    setEditOpen(true);
  }

  async function openHistory(doc: ISODocument) {
    setHistoryDoc(doc);
    setHistoryOpen(true);
    setLogsLoading(true);
    try {
      const res = await fetch(`/api/iso/documents/${doc.id}/logs`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load history");
      const data = await res.json();
      setLogs(data.data ?? []);
    } catch {
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  }

  async function handleSave() {
    if (!editing && (!form.code.trim() || !form.title.trim())) {
      setFormError("Code and Title are required."); return;
    }
    setSaving(true); setFormError(null);
    try {
      if (editing) {
        const res = await fetch(`/api/iso/documents/${editing.id}`, {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            status:        form.status,
            owner:         form.owner    || undefined,
            notes:         form.notes    || undefined,
            review_date:   form.reviewDate || undefined,
            version:       form.version  || undefined,
            last_reviewed: form.lastReviewed || undefined,
            content:       form.content  || undefined,
            author:        form.author   || undefined,
            reviewer:      form.reviewer || undefined,
            approver:      form.approver || undefined,
          }),
        });
        if (!res.ok) throw new Error("Save failed");
      } else {
        const res = await fetch("/api/iso/documents", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            code:        form.code,
            title:       form.title,
            category:    form.category,
            status:      form.status,
            owner:       form.owner || undefined,
            version:     form.version || undefined,
            review_date: form.reviewDate || undefined,
            notes:       form.notes || undefined,
          }),
        });
        if (!res.ok) throw new Error("Save failed");
      }
      await load();
      setEditOpen(false);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  function triggerUpload(docId: string) {
    uploadTargetRef.current = docId;
    fileInputRef.current?.click();
  }

  async function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    const docId = uploadTargetRef.current;
    if (!file || !docId) return;
    e.target.value = "";

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (!allowedTypes.includes(file.type)) {
      alert("Only PDF and Word (.docx, .doc) files are supported."); return;
    }
    if (file.size > 50 * 1024 * 1024) {
      alert("File must be under 50 MB."); return;
    }

    setUploadingId(docId);
    try {
      const res = await fetch(
        `/api/iso/documents/${docId}/upload?filename=${encodeURIComponent(file.name)}`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": file.type || "application/octet-stream" },
          body: file,
        }
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.message || "Upload failed");
      }
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploadingId(null);
    }
  }

  function viewFile(docId: string) {
    window.open(`/api/iso/documents/${docId}/file`, "_blank");
  }

  const allCategories = [
    "All",
    "policy", "procedure", "plan",
    "isms", "bcp", "audit", "training", "guide",
    "register", "log", "minutes", "work_instruction", "context",
  ];
  const categoryLabel: Record<string, string> = { work_instruction: "Work Instruction", All: "All Types" };
  const filtered = docs.filter((d) => {
    if (catFilter !== "All" && d.category !== catFilter) return false;
    if (statusFilter !== "All" && d.status !== statusFilter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      if (!d.title.toLowerCase().includes(q) && !d.code.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  if (loading) return <Spinner />;
  if (error)   return <ErrorMsg message={error} />;

  return (
    <div className="space-y-4">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.doc,.docx"
        className="hidden"
        onChange={handleFileSelected}
      />

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2 flex-wrap items-center">
          {/* Search */}
          <div className="relative">
            <BookOpen size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none" />
            <input
              type="text"
              placeholder="Search title or code…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`${inputCls} pl-8 w-48`}
            />
          </div>
          <select className={`${selectCls} w-auto`} value={catFilter} onChange={(e) => setCatFilter(e.target.value)}>
            {allCategories.map((v) => (
              <option key={v} value={v}>{categoryLabel[v] ?? cap(v)}</option>
            ))}
          </select>
          <select className={`${selectCls} w-auto`} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            {["All", "draft", "review", "approved", "superseded"].map((v) => (
              <option key={v} value={v}>{v === "All" ? "All Statuses" : cap(v)}</option>
            ))}
          </select>
          <span className="text-xs text-white/30">{filtered.length} of {docs.length}</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setIndexOpen(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/60 hover:text-white text-sm transition-colors"
            title="Master Document Index"
          >
            <TableProperties size={13} /> Index
          </button>
          <button
            onClick={() => setImportOpen(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/60 hover:text-white text-sm transition-colors"
            title="Import Word / PDF document"
          >
            <FilePlus2 size={13} /> Import
          </button>
          <button
            onClick={openNew}
            className="flex items-center gap-2 bg-[#7B1A1A] hover:bg-[#9B2A2A] text-white rounded-lg px-4 py-2 text-sm transition-colors"
          >
            <Plus size={14} /> Add Document
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white/4 border border-white/8 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/8">
                {["Title", "Code", "Type", "Version", "Status", "Next Review", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-white/40 text-sm">
                    {search || catFilter !== "All" || statusFilter !== "All" ? "No documents match your filters." : "No documents found."}
                  </td>
                </tr>
              ) : (
                filtered.map((doc, i) => (
                  <tr key={doc.id} className={`group ${i < filtered.length - 1 ? "border-b border-white/5" : ""}`}>
                    {/* Title — clickable, opens viewer */}
                    <td className="px-4 py-3 max-w-[260px]">
                      <button
                        onClick={() => openView(doc)}
                        className="text-left w-full"
                        title="View document"
                      >
                        <span className="block text-white/90 text-sm font-medium truncate group-hover:text-white transition-colors leading-snug">{doc.title}</span>
                        {doc.folder && <span className="block text-white/30 text-xs truncate mt-0.5">{doc.folder}</span>}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-white/55 font-mono text-xs whitespace-nowrap">{doc.code}</td>
                    <td className="px-4 py-3 whitespace-nowrap"><CategoryBadge category={doc.category} /></td>
                    <td className="px-4 py-3 text-white/50 text-xs whitespace-nowrap">v{doc.version}</td>
                    <td className="px-4 py-3 whitespace-nowrap"><DocStatusBadge status={doc.status} /></td>
                    <td className="px-4 py-3 whitespace-nowrap"><ReviewDateCell reviewDate={doc.reviewDate} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {/* View document */}
                        <button
                          onClick={() => openView(doc)}
                          className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs text-white/55 hover:text-white hover:bg-white/8 transition-colors"
                          title="Read document content"
                        >
                          <Eye size={11} />
                          View
                        </button>
                        {/* Edit document */}
                        <button
                          onClick={() => openContentEdit(doc)}
                          className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs text-white/55 hover:text-[#C05A5A] hover:bg-[#7B1A1A]/15 transition-colors"
                          title="Edit document content"
                        >
                          <Edit2 size={11} />
                          Edit
                        </button>
                        {/* Divider */}
                        <div className="w-px h-3.5 bg-white/8 mx-0.5" />
                        {/* Upload attachment */}
                        <button
                          onClick={() => triggerUpload(doc.id)}
                          disabled={uploadingId === doc.id}
                          className="p-1 rounded text-white/25 hover:text-green-400 transition-colors disabled:opacity-40"
                          title={doc.fileUrl ? `Replace: ${doc.fileName}` : "Upload file attachment"}
                        >
                          {uploadingId === doc.id
                            ? <div className="w-3 h-3 rounded-full border border-white/40 border-t-green-400 animate-spin" />
                            : <Upload size={11} />}
                        </button>
                        {/* Metadata */}
                        <button
                          onClick={() => openEdit(doc)}
                          className="p-1 rounded text-white/25 hover:text-white transition-colors"
                          title="Edit metadata (status, owner, version…)"
                        >
                          <ChevronRight size={11} />
                        </button>
                        {/* Traceability */}
                        <button
                          onClick={() => { setTraceDoc(doc); setTraceOpen(true); }}
                          className="p-1 rounded text-white/25 hover:text-blue-400 transition-colors"
                          title="Traceability — linked risks & audits"
                        >
                          <Link2 size={11} />
                        </button>
                        {/* History */}
                        <button
                          onClick={() => openHistory(doc)}
                          className="p-1 rounded text-white/25 hover:text-purple-400 transition-colors"
                          title="Edit history"
                        >
                          <History size={11} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit panel */}
      <SlidePanel open={editOpen} onClose={() => setEditOpen(false)} title={editing ? `Edit: ${editing.code}` : "Add Document"}>
        {editing && (
          <div className="px-3 py-2 rounded-lg bg-white/4 border border-white/8 text-xs text-white/50 space-y-0.5 -mt-2">
            <div className="font-medium text-white/70">{editing.title}</div>
            <div>{editing.folder ?? editing.category}</div>
          </div>
        )}
        {!editing && (
          <>
            <Field label="Code" required>
              <input className={inputCls} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. IS-01" />
            </Field>
            <Field label="Title" required>
              <input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Document title" />
            </Field>
            <Field label="Category">
              <select className={selectCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {[
                  "policy", "procedure", "plan",
                  "isms", "bcp", "audit", "training", "guide",
                  "register", "log", "minutes", "work_instruction", "context",
                ].map((v) => (
                  <option key={v} value={v}>{categoryLabel[v] ?? cap(v)}</option>
                ))}
              </select>
            </Field>
          </>
        )}
        <Field label="Status">
          <select className={selectCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            {["draft", "review", "approved", "superseded"].map((v) => (
              <option key={v} value={v}>{cap(v)}</option>
            ))}
          </select>
        </Field>
        <Field label="Owner">
          <input className={inputCls} value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} placeholder="Document owner" />
        </Field>
        <Field label="Author">
          <input className={inputCls} value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} placeholder="Document author" />
        </Field>
        <Field label="Reviewer">
          <input className={inputCls} value={form.reviewer} onChange={(e) => setForm({ ...form, reviewer: e.target.value })} placeholder="Reviewer name" />
        </Field>
        <Field label="Approver">
          <input className={inputCls} value={form.approver} onChange={(e) => setForm({ ...form, approver: e.target.value })} placeholder="Approver (sign-off)" />
        </Field>
        <Field label="Version">
          <input className={inputCls} value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} placeholder="1.0" />
        </Field>
        <Field label="Review Date">
          <input type="date" className={inputCls} value={form.reviewDate} onChange={(e) => setForm({ ...form, reviewDate: e.target.value })} />
        </Field>
        {editing && (
          <Field label="Last Reviewed">
            <input type="date" className={inputCls} value={form.lastReviewed} onChange={(e) => setForm({ ...form, lastReviewed: e.target.value })} />
          </Field>
        )}
        <Field label="Notes">
          <textarea rows={3} className={textareaCls} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes" />
        </Field>
        <Field label="Document Content">
          <textarea
            rows={14}
            className={textareaCls}
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
            placeholder="Full document content — imported from the ISO pack or typed here"
          />
        </Field>
        {editing && editing.fileName && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/4 border border-white/8 text-xs text-white/60">
            <FileText size={12} />
            <span className="truncate">{editing.fileName}</span>
          </div>
        )}
        {formError && <ErrorMsg message={formError} />}
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-[#7B1A1A] hover:bg-[#9B2A2A] disabled:opacity-50 text-white rounded-lg px-4 py-2.5 text-sm font-medium transition-colors"
        >
          {saving ? "Saving…" : editing ? "Save Changes" : "Create Document"}
        </button>
      </SlidePanel>

      {/* History panel */}
      <SlidePanel open={historyOpen} onClose={() => setHistoryOpen(false)} title={historyDoc ? `History: ${historyDoc.code}` : "Edit History"}>
        {historyDoc && (
          <p className="text-xs text-white/50 -mt-2">{historyDoc.title}</p>
        )}
        {logsLoading ? (
          <Spinner />
        ) : logs.length === 0 ? (
          <p className="text-xs text-white/40 text-center py-8">No edit history recorded yet.</p>
        ) : (
          <div className="space-y-3">
            {logs.map((log) => (
              <div key={log.id} className="relative pl-5 before:absolute before:left-1.5 before:top-2 before:w-1.5 before:h-1.5 before:rounded-full before:bg-[#7B1A1A]">
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-xs font-semibold uppercase tracking-wide ${
                    log.action === "upload" ? "text-green-400" : log.action === "create" ? "text-blue-400" : "text-white/70"
                  }`}>
                    {log.action}
                    {log.field ? ` · ${log.field}` : ""}
                  </span>
                  <span className="text-xs text-white/30 whitespace-nowrap">{fmtDateTime(log.createdAt)}</span>
                </div>
                {log.new_value && (
                  <p className="text-xs text-white/60 mt-0.5 break-words">
                    {log.old_value ? <span className="text-white/30 line-through mr-2">{log.old_value}</span> : null}
                    {log.new_value}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </SlidePanel>

      {/* Full-screen content editor */}
      {editorDoc && (
        <DocumentEditorModal
          key={`${editorDoc.id}-${editorStartEdit}`}
          doc={editorDoc}
          defaultEditing={editorStartEdit}
          onClose={() => setEditorDoc(null)}
          onSaved={() => { load(); setEditorDoc(null); }}
        />
      )}

      {/* Import document modal */}
      {importOpen && (
        <ImportDocumentModal
          onClose={() => setImportOpen(false)}
          onImported={(docId) => {
            setImportOpen(false);
            load().then(() => {
              /* If we got a docId back, open it immediately in the viewer */
              if (docId) {
                setDocs((prev) => {
                  const found = prev.find((d) => d.id === docId);
                  if (found) { setEditorStartEdit(false); setEditorDoc(found); }
                  return prev;
                });
              }
            });
          }}
        />
      )}

      {/* Master index modal */}
      {indexOpen && (
        <MasterIndexModal onClose={() => setIndexOpen(false)} />
      )}

      {/* Traceability panel */}
      {traceDoc && (
        <TraceabilityPanel
          doc={traceDoc}
          open={traceOpen}
          onClose={() => { setTraceOpen(false); setTraceDoc(null); }}
        />
      )}
    </div>
  );
}

// ─── TAB 3: Controls ──────────────────────────────────────────────────────────

function ControlsTab() {
  const [controls,  setControls]  = useState<ISOControl[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing,   setEditing]   = useState<ISOControl | null>(null);
  const [saving,    setSaving]    = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  type CtrlForm = { applicable: boolean; status: string; owner: string; evidence: string; justification: string };
  const [form, setForm] = useState<CtrlForm>({ applicable: true, status: "not-started", owner: "", evidence: "", justification: "" });

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch("/api/iso/controls", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load controls");
      const data = await res.json();
      setControls(data.data ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Load failed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function openEdit(ctrl: ISOControl) {
    setEditing(ctrl);
    setForm({
      applicable:   ctrl.applicable,
      status:       ctrl.status,
      owner:        ctrl.owner ?? "",
      evidence:     ctrl.evidence ?? "",
      justification: ctrl.justification ?? "",
    });
    setFormError(null);
    setPanelOpen(true);
  }

  async function handleSave() {
    if (!editing) return;
    setSaving(true); setFormError(null);
    try {
      const res = await fetch(`/api/iso/controls/${editing.id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Save failed");
      await load();
      setPanelOpen(false);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  const cats = ["Organizational", "People", "Physical", "Technological"] as const;
  const grouped: Record<string, ISOControl[]> = {};
  for (const cat of cats) grouped[cat] = controls.filter((c) => c.category === cat);

  if (loading) return <Spinner />;
  if (error)   return <ErrorMsg message={error} />;

  return (
    <div className="space-y-4">
      {cats.map((cat) => {
        const group      = grouped[cat];
        const applicable = group.filter((c) => c.applicable);
        const implemented = applicable.filter((c) => c.status === "implemented").length;
        const pct        = applicable.length > 0 ? Math.round((implemented / applicable.length) * 100) : 0;
        const isOpen     = !collapsed[cat];

        return (
          <div key={cat} className="bg-white/4 border border-white/8 rounded-xl overflow-hidden">
            <button
              onClick={() => setCollapsed((p) => ({ ...p, [cat]: !p[cat] }))}
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/4 transition-colors"
            >
              <div className="flex items-center gap-3">
                {isOpen ? <ChevronDown size={16} className="text-white/50" /> : <ChevronRight size={16} className="text-white/50" />}
                <span className="font-semibold text-white text-sm">{cat}</span>
                <span className="text-white/40 text-xs">{group.length} controls</span>
              </div>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${pct >= 80 ? "bg-green-500/15 text-green-400 border-green-500/30" : pct >= 50 ? "bg-yellow-500/15 text-yellow-400 border-yellow-500/30" : "bg-red-500/15 text-red-400 border-red-500/30"}`}>
                {pct}% implemented
              </span>
            </button>

            {isOpen && (
              <div className="border-t border-white/8 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/8">
                      {["Control ID", "Name", "Applicable", "Status", "Owner", ""].map((h) => (
                        <th key={h} className="px-4 py-2.5 text-left text-xs font-medium text-white/50 uppercase tracking-wide whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {group.map((ctrl, i) => (
                      <tr key={ctrl.id} className={i < group.length - 1 ? "border-b border-white/5" : ""}>
                        <td className="px-4 py-3 text-white/70 font-mono text-xs whitespace-nowrap">{ctrl.controlId}</td>
                        <td className="px-4 py-3 text-white/80 max-w-[240px] truncate" title={ctrl.name}>{ctrl.name}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-medium ${ctrl.applicable ? "text-green-400" : "text-white/30"}`}>
                            {ctrl.applicable ? "Yes" : "No"}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap"><ControlStatusBadge status={ctrl.status} /></td>
                        <td className="px-4 py-3 text-white/60 whitespace-nowrap text-xs">{ctrl.owner || "—"}</td>
                        <td className="px-4 py-3">
                          <button onClick={() => openEdit(ctrl)} className="text-white/40 hover:text-white transition-colors" title="Edit">
                            <Edit2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      <SlidePanel open={panelOpen} onClose={() => setPanelOpen(false)} title={editing ? `Edit: ${editing.controlId}` : "Edit Control"}>
        {editing && <p className="text-xs text-white/50 -mt-2 mb-2">{editing.name}</p>}
        <Field label="Applicable">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setForm({ ...form, applicable: !form.applicable })}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${form.applicable ? "bg-[#7B1A1A]" : "bg-white/15"}`}
            >
              <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${form.applicable ? "translate-x-4" : "translate-x-1"}`} />
            </button>
            <span className="text-sm text-white/70">{form.applicable ? "Yes" : "No"}</span>
          </div>
        </Field>
        <Field label="Status">
          <select className={selectCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            {[
              { v: "not-started",    l: "Not Started" },
              { v: "in-progress",    l: "In Progress" },
              { v: "implemented",    l: "Implemented" },
              { v: "not-applicable", l: "Not Applicable" },
            ].map(({ v, l }) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
        <Field label="Owner">
          <input className={inputCls} value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} placeholder="Control owner" />
        </Field>
        <Field label="Evidence">
          <textarea rows={3} className={textareaCls} value={form.evidence} onChange={(e) => setForm({ ...form, evidence: e.target.value })} placeholder="Evidence of implementation" />
        </Field>
        <Field label="Justification">
          <textarea rows={3} className={textareaCls} value={form.justification} onChange={(e) => setForm({ ...form, justification: e.target.value })} placeholder="Justification if not applicable" />
        </Field>
        {formError && <ErrorMsg message={formError} />}
        <button onClick={handleSave} disabled={saving} className="w-full bg-[#7B1A1A] hover:bg-[#9B2A2A] disabled:opacity-50 text-white rounded-lg px-4 py-2.5 text-sm font-medium transition-colors">
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </SlidePanel>
    </div>
  );
}

// ─── TAB 4: Risk Register ─────────────────────────────────────────────────────

function RisksTab() {
  const [risks,     setRisks]    = useState<ISORisk[]>([]);
  const [loading,   setLoading]  = useState(true);

  // Controls linking panel
  const [ctrlsRisk,    setCtrlsRisk]    = useState<ISORisk | null>(null);
  const [ctrlsOpen,    setCtrlsOpen]    = useState(false);
  const [linkedCtrls,  setLinkedCtrls]  = useState<ISOControl[]>([]);
  const [allCtrls,     setAllCtrls]     = useState<ISOControl[]>([]);
  const [ctrlsLoading, setCtrlsLoading] = useState(false);
  const [addCtrl,      setAddCtrl]      = useState("");
  const [linkingCtrl,  setLinkingCtrl]  = useState(false);
  const [unlinkingCtrl,setUnlinkingCtrl]= useState<string | null>(null);

  async function openCtrlsPanel(risk: ISORisk) {
    setCtrlsRisk(risk);
    setCtrlsOpen(true);
    setCtrlsLoading(true);
    try {
      const [lRes, cRes] = await Promise.all([
        fetch(`/api/iso/risks/${risk.id}/links`, { credentials: "include" }),
        fetch("/api/iso/controls", { credentials: "include" }),
      ]);
      if (lRes.ok) { const d = await lRes.json(); setLinkedCtrls(d.data.controls ?? []); }
      if (cRes.ok) { const d = await cRes.json(); setAllCtrls(d.data ?? []); }
    } finally { setCtrlsLoading(false); }
  }

  async function handleLinkCtrl() {
    if (!ctrlsRisk || !addCtrl) return;
    setLinkingCtrl(true);
    try {
      await fetch(`/api/iso/risks/${ctrlsRisk.id}/controls`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ control_id: addCtrl }),
      });
      setAddCtrl("");
      const res = await fetch(`/api/iso/risks/${ctrlsRisk.id}/links`, { credentials: "include" });
      if (res.ok) { const d = await res.json(); setLinkedCtrls(d.data.controls ?? []); }
    } finally { setLinkingCtrl(false); }
  }

  async function handleUnlinkCtrl(controlId: string) {
    if (!ctrlsRisk) return;
    setUnlinkingCtrl(controlId);
    try {
      await fetch(`/api/iso/risks/${ctrlsRisk.id}/controls/${controlId}`, { method: "DELETE", credentials: "include" });
      setLinkedCtrls((prev) => prev.filter((c) => c.id !== controlId));
    } finally { setUnlinkingCtrl(null); }
  }
  const [error,     setError]    = useState<string | null>(null);
  const [panelOpen, setPanelOpen]= useState(false);
  const [editing,   setEditing]  = useState<ISORisk | null>(null);
  const [saving,    setSaving]   = useState(false);
  const [deleting,  setDeleting] = useState<string | null>(null);
  const [formError, setFormError]= useState<string | null>(null);

  type RiskForm = {
    asset: string; threat: string; vulnerability: string;
    likelihood: number; impact: number;
    treatment: string; controlMeasure: string; owner: string; status: string;
  };
  const emptyForm: RiskForm = {
    asset: "", threat: "", vulnerability: "",
    likelihood: 1, impact: 1,
    treatment: "mitigate", controlMeasure: "", owner: "", status: "open",
  };
  const [form, setForm] = useState<RiskForm>(emptyForm);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch("/api/iso/risks", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load risks");
      const data = await res.json();
      setRisks(data.data ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Load failed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function openNew() {
    setEditing(null); setForm(emptyForm); setFormError(null); setPanelOpen(true);
  }

  function openEdit(risk: ISORisk) {
    setEditing(risk);
    setForm({
      asset:          risk.asset,
      threat:         risk.threat,
      vulnerability:  risk.vulnerability,
      likelihood:     risk.likelihood,
      impact:         risk.impact,
      treatment:      risk.treatment,
      controlMeasure: risk.controlMeasure ?? "",
      owner:          risk.owner ?? "",
      status:         risk.status,
    });
    setFormError(null); setPanelOpen(true);
  }

  async function handleSave() {
    if (!form.asset.trim() || !form.threat.trim()) {
      setFormError("Asset and Threat are required."); return;
    }
    setSaving(true); setFormError(null);
    try {
      const url    = editing ? `/api/iso/risks/${editing.id}` : "/api/iso/risks";
      const method = editing ? "PATCH" : "POST";
      const res    = await fetch(url, {
        method, credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          asset:           form.asset,
          threat:          form.threat,
          vulnerability:   form.vulnerability,
          likelihood:      form.likelihood,
          impact:          form.impact,
          risk_score:      form.likelihood * form.impact,
          treatment:       form.treatment,
          control_measure: form.controlMeasure || undefined,
          owner:           form.owner || undefined,
          status:          form.status,
        }),
      });
      if (!res.ok) throw new Error("Save failed");
      await load();
      setPanelOpen(false);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm("Delete this risk? This cannot be undone.")) return;
    setDeleting(id);
    try {
      await fetch(`/api/iso/risks/${id}`, { method: "DELETE", credentials: "include" });
      await load();
    } catch { /* silent */ }
    finally { setDeleting(null); }
  }

  const score = form.likelihood * form.impact;

  if (loading) return <Spinner />;
  if (error)   return <ErrorMsg message={error} />;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={openNew} className="flex items-center gap-2 bg-[#7B1A1A] hover:bg-[#9B2A2A] text-white rounded-lg px-4 py-2 text-sm transition-colors">
          <Plus size={14} /> Add Risk
        </button>
      </div>

      <div className="bg-white/4 border border-white/8 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/8">
                {["Risk ID", "Asset", "Threat", "L", "I", "Score", "Treatment", "Status", "Owner", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {risks.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-8 text-center text-white/40 text-sm">No risks recorded</td></tr>
              ) : (
                risks.map((risk, i) => (
                  <tr key={risk.id} className={i < risks.length - 1 ? "border-b border-white/5" : ""}>
                    <td className="px-4 py-3 text-white/70 font-mono text-xs whitespace-nowrap">{risk.riskId}</td>
                    <td className="px-4 py-3 text-white/80 max-w-[120px] truncate">{risk.asset}</td>
                    <td className="px-4 py-3 text-white/70 max-w-[150px] truncate">{risk.threat}</td>
                    <td className="px-4 py-3 text-white/60 text-center">{risk.likelihood}</td>
                    <td className="px-4 py-3 text-white/60 text-center">{risk.impact}</td>
                    <td className="px-4 py-3"><RiskScoreBadge score={risk.score} /></td>
                    <td className="px-4 py-3 text-white/60 whitespace-nowrap text-xs">{cap(risk.treatment)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                        risk.status === "open" ? "bg-red-500/15 text-red-400 border-red-500/30" :
                        risk.status === "in-treatment" ? "bg-yellow-500/15 text-yellow-400 border-yellow-500/30" :
                        "bg-green-500/15 text-green-400 border-green-500/30"
                      }`}>
                        {risk.status === "in-treatment" ? "In Treatment" : cap(risk.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-white/60 whitespace-nowrap text-xs">{risk.owner || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => openCtrlsPanel(risk)}
                          className="flex items-center gap-1 px-2 py-1 rounded text-xs text-white/40 hover:text-blue-400 hover:bg-blue-500/10 transition-colors"
                          title="Link controls to this risk"
                        >
                          <ShieldCheck size={11} /> Controls
                        </button>
                        <button onClick={() => openEdit(risk)} className="p-1 rounded text-white/30 hover:text-white transition-colors" title="Edit"><Edit2 size={13} /></button>
                        <button onClick={() => handleDelete(risk.id)} disabled={deleting === risk.id} className="p-1 rounded text-white/30 hover:text-red-400 transition-colors disabled:opacity-40" title="Delete"><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <SlidePanel open={panelOpen} onClose={() => setPanelOpen(false)} title={editing ? "Edit Risk" : "Add Risk"}>
        <Field label="Asset" required>
          <input className={inputCls} value={form.asset} onChange={(e) => setForm({ ...form, asset: e.target.value })} placeholder="Affected asset" />
        </Field>
        <Field label="Threat" required>
          <input className={inputCls} value={form.threat} onChange={(e) => setForm({ ...form, threat: e.target.value })} placeholder="Threat description" />
        </Field>
        <Field label="Vulnerability">
          <input className={inputCls} value={form.vulnerability} onChange={(e) => setForm({ ...form, vulnerability: e.target.value })} placeholder="Vulnerability exploited" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Likelihood (1–5)">
            <select className={selectCls} value={form.likelihood} onChange={(e) => setForm({ ...form, likelihood: Number(e.target.value) })}>
              {[1, 2, 3, 4, 5].map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </Field>
          <Field label="Impact (1–5)">
            <select className={selectCls} value={form.impact} onChange={(e) => setForm({ ...form, impact: Number(e.target.value) })}>
              {[1, 2, 3, 4, 5].map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </Field>
        </div>
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white/5 border border-white/8">
          <span className="text-sm text-white/60">Risk Score:</span>
          <RiskScoreBadge score={score} />
        </div>
        <Field label="Treatment">
          <select className={selectCls} value={form.treatment} onChange={(e) => setForm({ ...form, treatment: e.target.value })}>
            {["mitigate", "accept", "transfer", "avoid"].map((v) => (
              <option key={v} value={v}>{cap(v)}</option>
            ))}
          </select>
        </Field>
        <Field label="Control Measure">
          <textarea rows={3} className={textareaCls} value={form.controlMeasure} onChange={(e) => setForm({ ...form, controlMeasure: e.target.value })} placeholder="Describe the control or treatment measure" />
        </Field>
        <Field label="Owner">
          <input className={inputCls} value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} placeholder="Risk owner" />
        </Field>
        {editing && (
          <Field label="Status">
            <select className={selectCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              {[
                { v: "open", l: "Open" }, { v: "in-treatment", l: "In Treatment" },
                { v: "accepted", l: "Accepted" }, { v: "closed", l: "Closed" },
              ].map(({ v, l }) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
        )}
        {formError && <ErrorMsg message={formError} />}
        <button onClick={handleSave} disabled={saving} className="w-full bg-[#7B1A1A] hover:bg-[#9B2A2A] disabled:opacity-50 text-white rounded-lg px-4 py-2.5 text-sm font-medium transition-colors">
          {saving ? "Saving…" : editing ? "Save Changes" : "Create Risk"}
        </button>
      </SlidePanel>

      {/* Controls linking panel */}
      <SlidePanel
        open={ctrlsOpen}
        onClose={() => { setCtrlsOpen(false); setCtrlsRisk(null); }}
        title={ctrlsRisk ? `Controls: ${ctrlsRisk.riskId}` : "Linked Controls"}
      >
        {ctrlsRisk && (
          <div className="px-3 py-2 rounded-lg bg-white/4 border border-white/8 text-xs -mt-2">
            <p className="text-white/70 font-medium truncate">{ctrlsRisk.threat}</p>
            <p className="text-white/40 truncate">{ctrlsRisk.asset}</p>
          </div>
        )}

        {ctrlsLoading ? (
          <Spinner />
        ) : (
          <>
            <div className="space-y-1.5">
              {linkedCtrls.length === 0 && (
                <p className="text-xs text-white/30 italic">No controls linked yet.</p>
              )}
              {linkedCtrls.map((ctrl) => (
                <div key={ctrl.id} className="flex items-start gap-2 px-3 py-2 rounded-lg bg-white/4 border border-white/8 group">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-mono text-xs text-white/50">{ctrl.controlId}</span>
                      <ControlStatusBadge status={ctrl.status} />
                    </div>
                    <p className="text-xs text-white/70 leading-snug">{ctrl.name}</p>
                  </div>
                  <button
                    onClick={() => handleUnlinkCtrl(ctrl.id)}
                    disabled={unlinkingCtrl === ctrl.id}
                    className="p-1 text-white/20 hover:text-red-400 transition-colors disabled:opacity-40 flex-shrink-0 opacity-0 group-hover:opacity-100"
                  >
                    {unlinkingCtrl === ctrl.id
                      ? <div className="w-3 h-3 rounded-full border border-white/30 border-t-red-400 animate-spin" />
                      : <Unlink size={11} />}
                  </button>
                </div>
              ))}
            </div>

            {/* Add control */}
            {allCtrls.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-white/8">
                <p className="text-xs text-white/50 font-medium">Link a control:</p>
                <select
                  className={`${selectCls} text-xs`}
                  value={addCtrl}
                  onChange={(e) => setAddCtrl(e.target.value)}
                >
                  <option value="">— select control —</option>
                  {(["Organizational", "People", "Physical", "Technological"] as const).map((cat) => (
                    <optgroup key={cat} label={cat}>
                      {allCtrls
                        .filter((c) => c.category === cat && !linkedCtrls.find((l) => l.id === c.id))
                        .map((c) => (
                          <option key={c.id} value={c.id}>{c.controlId} — {c.name}</option>
                        ))}
                    </optgroup>
                  ))}
                </select>
                <button
                  onClick={handleLinkCtrl}
                  disabled={!addCtrl || linkingCtrl}
                  className="w-full flex items-center justify-center gap-2 bg-[#7B1A1A] hover:bg-[#9B2A2A] disabled:opacity-40 text-white rounded-lg py-2 text-xs font-medium transition-colors"
                >
                  <Link2 size={12} /> {linkingCtrl ? "Linking…" : "Link Control"}
                </button>
              </div>
            )}
          </>
        )}
      </SlidePanel>
    </div>
  );
}

// ─── TAB 5: Incidents ─────────────────────────────────────────────────────────

function IncidentsTab() {
  const [incidents, setIncidents] = useState<ISOIncident[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing,   setEditing]   = useState<ISOIncident | null>(null);
  const [saving,    setSaving]    = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  type IncidentForm = {
    title: string; description: string; severity: string;
    reporter: string; occurredAt: string; assignedTo: string;
    status: string; resolvedAt: string; correctiveAction: string;
  };
  const emptyForm: IncidentForm = {
    title: "", description: "", severity: "medium",
    reporter: "", occurredAt: "", assignedTo: "",
    status: "open", resolvedAt: "", correctiveAction: "",
  };
  const [form, setForm] = useState<IncidentForm>(emptyForm);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch("/api/iso/incidents", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load incidents");
      const data = await res.json();
      setIncidents(data.data ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Load failed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function openNew() {
    setEditing(null); setForm(emptyForm); setFormError(null); setPanelOpen(true);
  }

  function openEdit(inc: ISOIncident) {
    setEditing(inc);
    setForm({
      title:            inc.title,
      description:      inc.description ?? "",
      severity:         inc.severity,
      reporter:         inc.reporter ?? "",
      occurredAt:       inc.occurredAt ? inc.occurredAt.slice(0, 16) : "",
      assignedTo:       inc.assignedTo ?? "",
      status:           inc.status,
      resolvedAt:       inc.resolvedAt ? inc.resolvedAt.slice(0, 16) : "",
      correctiveAction: inc.correctiveAction ?? "",
    });
    setFormError(null); setPanelOpen(true);
  }

  async function handleSave() {
    if (!form.title.trim()) { setFormError("Title is required."); return; }
    setSaving(true); setFormError(null);
    try {
      const url    = editing ? `/api/iso/incidents/${editing.id}` : "/api/iso/incidents";
      const method = editing ? "PATCH" : "POST";
      const res    = await fetch(url, {
        method, credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title:             form.title,
          description:       form.description || undefined,
          severity:          form.severity,
          status:            form.status,
          reporter:          form.reporter || undefined,
          assigned_to:       form.assignedTo || undefined,
          occurred_at:       form.occurredAt || undefined,
          resolved_at:       form.resolvedAt || undefined,
          corrective_action: form.correctiveAction || undefined,
        }),
      });
      if (!res.ok) throw new Error("Save failed");
      await load();
      setPanelOpen(false);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Spinner />;
  if (error)   return <ErrorMsg message={error} />;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={openNew} className="flex items-center gap-2 bg-[#7B1A1A] hover:bg-[#9B2A2A] text-white rounded-lg px-4 py-2 text-sm transition-colors">
          <Plus size={14} /> Log Incident
        </button>
      </div>

      <div className="bg-white/4 border border-white/8 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/8">
                {["Incident ID", "Title", "Severity", "Status", "Reporter", "Occurred", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {incidents.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-white/40 text-sm">No incidents recorded</td></tr>
              ) : (
                incidents.map((inc, i) => (
                  <tr key={inc.id} className={i < incidents.length - 1 ? "border-b border-white/5" : ""}>
                    <td className="px-4 py-3 text-white/70 font-mono text-xs whitespace-nowrap">{inc.incidentId}</td>
                    <td className="px-4 py-3 text-white/80 max-w-[200px] truncate">{inc.title}</td>
                    <td className="px-4 py-3 whitespace-nowrap"><SeverityBadge severity={inc.severity} /></td>
                    <td className="px-4 py-3 whitespace-nowrap"><IncidentStatusBadge status={inc.status} /></td>
                    <td className="px-4 py-3 text-white/60 whitespace-nowrap text-xs">{inc.reporter || "—"}</td>
                    <td className="px-4 py-3 text-white/60 whitespace-nowrap text-xs">{fmtDate(inc.occurredAt)}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => openEdit(inc)} className="text-white/40 hover:text-white transition-colors" title="Edit"><Edit2 size={14} /></button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <SlidePanel open={panelOpen} onClose={() => setPanelOpen(false)} title={editing ? "Edit Incident" : "Log Incident"}>
        <Field label="Title" required>
          <input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Incident title" />
        </Field>
        <Field label="Description">
          <textarea rows={3} className={textareaCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe what happened" />
        </Field>
        <Field label="Severity">
          <select className={selectCls} value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}>
            {["critical", "high", "medium", "low"].map((v) => <option key={v} value={v}>{cap(v)}</option>)}
          </select>
        </Field>
        <Field label="Reporter">
          <input className={inputCls} value={form.reporter} onChange={(e) => setForm({ ...form, reporter: e.target.value })} placeholder="Who reported this" />
        </Field>
        <Field label="Occurred At">
          <input type="datetime-local" className={inputCls} value={form.occurredAt} onChange={(e) => setForm({ ...form, occurredAt: e.target.value })} />
        </Field>
        <Field label="Assigned To">
          <input className={inputCls} value={form.assignedTo} onChange={(e) => setForm({ ...form, assignedTo: e.target.value })} placeholder="Assigned handler" />
        </Field>
        {editing && (
          <>
            <Field label="Status">
              <select className={selectCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                {[
                  { v: "open", l: "Open" }, { v: "investigating", l: "Investigating" },
                  { v: "resolved", l: "Resolved" }, { v: "closed", l: "Closed" },
                ].map(({ v, l }) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Resolved At">
              <input type="datetime-local" className={inputCls} value={form.resolvedAt} onChange={(e) => setForm({ ...form, resolvedAt: e.target.value })} />
            </Field>
            <Field label="Corrective Action">
              <textarea rows={3} className={textareaCls} value={form.correctiveAction} onChange={(e) => setForm({ ...form, correctiveAction: e.target.value })} placeholder="Steps taken to prevent recurrence" />
            </Field>
          </>
        )}
        {formError && <ErrorMsg message={formError} />}
        <button onClick={handleSave} disabled={saving} className="w-full bg-[#7B1A1A] hover:bg-[#9B2A2A] disabled:opacity-50 text-white rounded-lg px-4 py-2.5 text-sm font-medium transition-colors">
          {saving ? "Saving…" : editing ? "Save Changes" : "Log Incident"}
        </button>
      </SlidePanel>
    </div>
  );
}

// ─── TAB 6: Audits ────────────────────────────────────────────────────────────

function AuditsTab() {
  const [audits,    setAudits]    = useState<ISOAudit[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing,   setEditing]   = useState<ISOAudit | null>(null);
  const [saving,    setSaving]    = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // ── Findings sub-panel ───────────────────────────────────────────────────
  const [findOpen,        setFindOpen]        = useState(false);
  const [findAudit,       setFindAudit]       = useState<ISOAudit | null>(null);
  const [findings,        setFindings]        = useState<ISOAuditFinding[]>([]);
  const [findLoading,     setFindLoading]     = useState(false);
  const [editingFinding,  setEditingFinding]  = useState<ISOAuditFinding | null>(null);
  const [savingFinding,   setSavingFinding]   = useState(false);
  const [deletingFinding, setDeletingFinding] = useState<string | null>(null);
  const [findFormOpen,    setFindFormOpen]    = useState(false);
  const [findFormErr,     setFindFormErr]     = useState<string | null>(null);

  type FindingForm = {
    title: string; severity: string; description: string; status: string;
    document_id: string; control_id: string; corrective_action: string; corrective_action_status: string;
  };
  const emptyFindForm: FindingForm = {
    title: "", severity: "observation", description: "", status: "open",
    document_id: "", control_id: "", corrective_action: "", corrective_action_status: "open",
  };
  const [findForm, setFindForm] = useState<FindingForm>(emptyFindForm);
  const [findDocs,     setFindDocs]     = useState<ISODocument[]>([]);
  const [findControls, setFindControls] = useState<ISOControl[]>([]);

  type AuditForm = {
    title: string; type: string; auditor: string; scope: string;
    plannedDate: string; actualDate: string; status: string;
    findings: string; nonconformities: number; observations: number;
  };
  const emptyForm: AuditForm = {
    title: "", type: "internal", auditor: "", scope: "",
    plannedDate: "", actualDate: "", status: "planned",
    findings: "", nonconformities: 0, observations: 0,
  };
  const [form, setForm] = useState<AuditForm>(emptyForm);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch("/api/iso/audits", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load audits");
      const data = await res.json();
      setAudits(data.data ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Load failed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function loadFindings(auditId: string) {
    setFindLoading(true);
    try {
      const res = await fetch(`/api/iso/audits/${auditId}/findings`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setFindings(data.data ?? []);
    } catch {
      setFindings([]);
    } finally {
      setFindLoading(false);
    }
  }

  function openFindings(audit: ISOAudit) {
    setPanelOpen(false);
    setFindAudit(audit);
    setFindOpen(true);
    setFindFormOpen(false);
    setEditingFinding(null);
    setFindForm(emptyFindForm);
    loadFindings(audit.id);
    // load docs + controls for dropdowns (best-effort)
    Promise.all([
      fetch("/api/iso/documents", { credentials: "include" }).then(r => r.json()),
      fetch("/api/iso/controls",  { credentials: "include" }).then(r => r.json()),
    ]).then(([d, c]) => {
      setFindDocs(d.data ?? []);
      setFindControls(c.data ?? []);
    }).catch(() => {});
  }

  function openAddFinding() {
    setEditingFinding(null);
    setFindForm(emptyFindForm);
    setFindFormErr(null);
    setFindFormOpen(true);
  }

  function openEditFinding(f: ISOAuditFinding) {
    setEditingFinding(f);
    setFindForm({
      title: f.title, severity: f.severity, description: f.description ?? "", status: f.status,
      document_id: f.documentId ?? "", control_id: f.controlId ?? "",
      corrective_action: f.correctiveAction ?? "", corrective_action_status: f.correctiveActionStatus ?? "open",
    });
    setFindFormErr(null);
    setFindFormOpen(true);
  }

  async function handleFindingSave() {
    if (!findAudit || !findForm.title.trim()) { setFindFormErr("Title is required."); return; }
    setSavingFinding(true); setFindFormErr(null);
    try {
      const url    = editingFinding ? `/api/iso/audits/${findAudit.id}/findings/${editingFinding.id}` : `/api/iso/audits/${findAudit.id}/findings`;
      const method = editingFinding ? "PATCH" : "POST";
      const res = await fetch(url, {
        method, credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: findForm.title, severity: findForm.severity,
          description: findForm.description || undefined, status: findForm.status,
          document_id: findForm.document_id || undefined,
          control_id: findForm.control_id || undefined,
          corrective_action: findForm.corrective_action || undefined,
          corrective_action_status: findForm.corrective_action_status,
        }),
      });
      if (!res.ok) throw new Error("Save failed");
      await loadFindings(findAudit.id);
      setFindFormOpen(false);
      setEditingFinding(null);
      setFindForm(emptyFindForm);
    } catch (e) {
      setFindFormErr(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSavingFinding(false);
    }
  }

  async function handleFindingDelete(fid: string) {
    if (!findAudit || !window.confirm("Delete this finding?")) return;
    setDeletingFinding(fid);
    try {
      await fetch(`/api/iso/audits/${findAudit.id}/findings/${fid}`, { method: "DELETE", credentials: "include" });
      await loadFindings(findAudit.id);
    } catch { /* silent */ }
    finally { setDeletingFinding(null); }
  }

  function openNew() {
    setEditing(null); setForm(emptyForm); setFormError(null); setPanelOpen(true);
  }

  function openEdit(audit: ISOAudit) {
    setEditing(audit);
    setForm({
      title:           audit.title,
      type:            audit.type,
      auditor:         audit.auditor ?? "",
      scope:           audit.scope ?? "",
      plannedDate:     audit.plannedDate ? audit.plannedDate.slice(0, 10) : "",
      actualDate:      audit.actualDate ? audit.actualDate.slice(0, 10) : "",
      status:          audit.status,
      findings:        audit.findings ?? "",
      nonconformities: audit.nonconformities ?? 0,
      observations:    audit.observations ?? 0,
    });
    setFormError(null); setPanelOpen(true);
  }

  async function handleSave() {
    if (!form.title.trim()) { setFormError("Title is required."); return; }
    setSaving(true); setFormError(null);
    try {
      const url    = editing ? `/api/iso/audits/${editing.id}` : "/api/iso/audits";
      const method = editing ? "PATCH" : "POST";
      const res    = await fetch(url, {
        method, credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          audit_type:      form.type,
          title:           form.title,
          auditor:         form.auditor || undefined,
          scope:           form.scope || undefined,
          planned_date:    form.plannedDate || undefined,
          actual_date:     form.actualDate || undefined,
          status:          form.status,
          findings:        form.findings || undefined,
          nonconformities: form.nonconformities,
          observations:    form.observations,
        }),
      });
      if (!res.ok) throw new Error("Save failed");
      await load();
      setPanelOpen(false);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  const majorCount    = findings.filter((f) => f.severity === "major"       && f.status === "open").length;
  const minorCount    = findings.filter((f) => f.severity === "minor"       && f.status === "open").length;
  const obsCount      = findings.filter((f) => f.severity === "observation" && f.status === "open").length;

  if (loading) return <Spinner />;
  if (error)   return <ErrorMsg message={error} />;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={openNew} className="flex items-center gap-2 bg-[#7B1A1A] hover:bg-[#9B2A2A] text-white rounded-lg px-4 py-2 text-sm transition-colors">
          <Plus size={14} /> Schedule Audit
        </button>
      </div>

      <div className="bg-white/4 border border-white/8 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/8">
                {["Title", "Type", "Auditor", "Planned Date", "Status", "NC", "Obs", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {audits.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-white/40 text-sm">No audits scheduled</td></tr>
              ) : (
                audits.map((audit, i) => (
                  <tr key={audit.id} className={i < audits.length - 1 ? "border-b border-white/5" : ""}>
                    <td className="px-4 py-3 text-white/80 max-w-[180px] truncate">{audit.title}</td>
                    <td className="px-4 py-3 text-white/60 whitespace-nowrap text-xs">{cap(audit.type)}</td>
                    <td className="px-4 py-3 text-white/60 whitespace-nowrap text-xs">{audit.auditor || "—"}</td>
                    <td className="px-4 py-3 text-white/60 whitespace-nowrap text-xs">{fmtDate(audit.plannedDate)}</td>
                    <td className="px-4 py-3 whitespace-nowrap"><AuditStatusBadge status={audit.status} /></td>
                    <td className="px-4 py-3 text-white/60 text-center text-xs">{audit.nonconformities ?? 0}</td>
                    <td className="px-4 py-3 text-white/60 text-center text-xs">{audit.observations ?? 0}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => openFindings(audit)}
                          className="flex items-center gap-1 px-2 py-1 rounded text-xs text-white/40 hover:text-purple-400 hover:bg-purple-500/10 transition-colors"
                          title="View / add findings"
                        >
                          <ListChecks size={12} /> Findings
                        </button>
                        <button onClick={() => openEdit(audit)} className="p-1 rounded text-white/30 hover:text-white transition-colors" title="Edit audit"><Edit2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Audit edit panel ── */}
      <SlidePanel open={panelOpen} onClose={() => setPanelOpen(false)} title={editing ? "Edit Audit" : "Schedule Audit"}>
        <Field label="Title" required>
          <input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Audit title" />
        </Field>
        <Field label="Type">
          <select className={selectCls} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
            {["internal", "external", "surveillance"].map((v) => <option key={v} value={v}>{cap(v)}</option>)}
          </select>
        </Field>
        <Field label="Auditor">
          <input className={inputCls} value={form.auditor} onChange={(e) => setForm({ ...form, auditor: e.target.value })} placeholder="Lead auditor name" />
        </Field>
        <Field label="Scope">
          <textarea rows={2} className={textareaCls} value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} placeholder="Audit scope" />
        </Field>
        <Field label="Planned Date">
          <input type="date" className={inputCls} value={form.plannedDate} onChange={(e) => setForm({ ...form, plannedDate: e.target.value })} />
        </Field>
        {editing && (
          <>
            <Field label="Actual Date">
              <input type="date" className={inputCls} value={form.actualDate} onChange={(e) => setForm({ ...form, actualDate: e.target.value })} />
            </Field>
            <Field label="Status">
              <select className={selectCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                {[
                  { v: "planned", l: "Planned" }, { v: "in-progress", l: "In Progress" }, { v: "complete", l: "Complete" },
                ].map(({ v, l }) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Summary Notes">
              <textarea rows={3} className={textareaCls} value={form.findings} onChange={(e) => setForm({ ...form, findings: e.target.value })} placeholder="Summary of audit findings" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nonconformities">
                <input type="number" min={0} className={inputCls} value={form.nonconformities} onChange={(e) => setForm({ ...form, nonconformities: Number(e.target.value) })} />
              </Field>
              <Field label="Observations">
                <input type="number" min={0} className={inputCls} value={form.observations} onChange={(e) => setForm({ ...form, observations: Number(e.target.value) })} />
              </Field>
            </div>
          </>
        )}
        {formError && <ErrorMsg message={formError} />}
        <button onClick={handleSave} disabled={saving} className="w-full bg-[#7B1A1A] hover:bg-[#9B2A2A] disabled:opacity-50 text-white rounded-lg px-4 py-2.5 text-sm font-medium transition-colors">
          {saving ? "Saving…" : editing ? "Save Changes" : "Schedule Audit"}
        </button>
      </SlidePanel>

      {/* ── Findings panel ── */}
      <SlidePanel open={findOpen} onClose={() => { setFindOpen(false); setFindFormOpen(false); }} title={findAudit ? `Findings: ${findAudit.title}` : "Findings"}>
        {/* Summary chips */}
        {findings.length > 0 && (
          <div className="flex flex-wrap gap-2 -mt-2">
            {majorCount > 0 && <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-500/15 text-red-400 border border-red-500/25">{majorCount} Major</span>}
            {minorCount > 0 && <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-500/15 text-yellow-400 border border-yellow-500/25">{minorCount} Minor</span>}
            {obsCount   > 0 && <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-500/15 text-blue-400 border border-blue-500/25">{obsCount} Observations</span>}
          </div>
        )}

        <button
          onClick={openAddFinding}
          className="w-full flex items-center justify-center gap-2 border border-dashed border-white/15 hover:border-white/30 hover:bg-white/4 rounded-lg px-3 py-2.5 text-xs text-white/50 hover:text-white transition-colors"
        >
          <Plus size={13} /> Add Finding
        </button>

        {/* Add / Edit finding form (inline) */}
        {findFormOpen && (
          <div className="border border-white/10 rounded-xl p-4 space-y-3 bg-white/3">
            <p className="text-xs font-semibold text-white/70">{editingFinding ? "Edit Finding" : "New Finding"}</p>
            <Field label="Title" required>
              <input className={inputCls} value={findForm.title} onChange={(e) => setFindForm({ ...findForm, title: e.target.value })} placeholder="Finding title" autoFocus />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Severity">
                <select className={selectCls} value={findForm.severity} onChange={(e) => setFindForm({ ...findForm, severity: e.target.value })}>
                  {[{ v: "major", l: "Major" }, { v: "minor", l: "Minor" }, { v: "observation", l: "Observation" }].map(({ v, l }) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </Field>
              <Field label="Status">
                <select className={selectCls} value={findForm.status} onChange={(e) => setFindForm({ ...findForm, status: e.target.value })}>
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                </select>
              </Field>
            </div>
            <Field label="Description">
              <textarea rows={3} className={textareaCls} value={findForm.description} onChange={(e) => setFindForm({ ...findForm, description: e.target.value })} placeholder="Details of the finding" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Linked Document">
                <select className={selectCls} value={findForm.document_id} onChange={(e) => setFindForm({ ...findForm, document_id: e.target.value })}>
                  <option value="">— none —</option>
                  {findDocs.map((d) => (
                    <option key={d.id} value={d.id}>{d.code} – {d.title}</option>
                  ))}
                </select>
              </Field>
              <Field label="Linked Control">
                <select className={selectCls} value={findForm.control_id} onChange={(e) => setFindForm({ ...findForm, control_id: e.target.value })}>
                  <option value="">— none —</option>
                  {(["Organizational","People","Physical","Technological"] as const).map((cat) => {
                    const items = findControls.filter((c) => c.category === cat);
                    if (!items.length) return null;
                    return (
                      <optgroup key={cat} label={cat}>
                        {items.map((c) => <option key={c.id} value={c.id}>{c.controlId} – {c.name}</option>)}
                      </optgroup>
                    );
                  })}
                </select>
              </Field>
            </div>
            <Field label="Corrective Action">
              <textarea rows={2} className={textareaCls} value={findForm.corrective_action} onChange={(e) => setFindForm({ ...findForm, corrective_action: e.target.value })} placeholder="Describe the corrective action taken or planned" />
            </Field>
            <Field label="Corrective Action Status">
              <select className={selectCls} value={findForm.corrective_action_status} onChange={(e) => setFindForm({ ...findForm, corrective_action_status: e.target.value })}>
                <option value="open">Open</option>
                <option value="in-progress">In Progress</option>
                <option value="closed">Closed</option>
              </select>
            </Field>
            {findFormErr && <ErrorMsg message={findFormErr} />}
            <div className="flex gap-2">
              <button onClick={() => { setFindFormOpen(false); setEditingFinding(null); }} className="flex-1 border border-white/10 text-white/50 hover:text-white rounded-lg py-2 text-xs transition-colors">
                Cancel
              </button>
              <button onClick={handleFindingSave} disabled={savingFinding} className="flex-1 bg-[#7B1A1A] hover:bg-[#9B2A2A] disabled:opacity-50 text-white rounded-lg py-2 text-xs font-medium transition-colors">
                {savingFinding ? "Saving…" : editingFinding ? "Save" : "Add Finding"}
              </button>
            </div>
          </div>
        )}

        {/* Findings list */}
        {findLoading ? (
          <Spinner />
        ) : findings.length === 0 ? (
          <p className="text-xs text-white/35 text-center py-6">No findings recorded for this audit.</p>
        ) : (
          <div className="space-y-2">
            {findings.map((f) => (
              <div key={f.id} className="rounded-xl border border-white/8 bg-white/3 p-3 space-y-1.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-white/35">{f.findingId}</span>
                    <FindingSeverityBadge severity={f.severity} />
                    <span className={`text-xs font-medium ${f.status === "closed" ? "text-white/30 line-through" : "text-white/70"}`}>
                      {f.title}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => openEditFinding(f)} className="p-1 text-white/25 hover:text-white transition-colors"><Edit2 size={11} /></button>
                    <button
                      onClick={() => handleFindingDelete(f.id)}
                      disabled={deletingFinding === f.id}
                      className="p-1 text-white/20 hover:text-red-400 transition-colors disabled:opacity-40"
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                </div>
                {f.description && <p className="text-xs text-white/50 leading-relaxed">{f.description}</p>}
                {/* traceability badges */}
                {(f.documentCode || f.controlCode) && (
                  <div className="flex flex-wrap gap-1.5 pt-0.5">
                    {f.documentCode && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-[#7B1A1A]/20 text-red-300 border border-red-500/20">
                        <FileText size={9} /> {f.documentCode}
                      </span>
                    )}
                    {f.controlCode && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-500/10 text-blue-300 border border-blue-500/20">
                        <ShieldCheck size={9} /> {f.controlCode}
                      </span>
                    )}
                  </div>
                )}
                {/* corrective action */}
                {f.correctiveAction && (
                  <div className="mt-1 p-2 rounded-lg bg-white/3 border border-white/6 space-y-1">
                    <p className="text-xs text-white/40 font-medium">Corrective Action</p>
                    <p className="text-xs text-white/60 leading-relaxed">{f.correctiveAction}</p>
                    <CorrectiveActionBadge status={f.correctiveActionStatus} />
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className={`text-xs ${f.status === "closed" ? "text-green-400/70" : "text-white/30"}`}>{cap(f.status)}</span>
                  <span className="text-xs text-white/25">{fmtDate(f.createdAt)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </SlidePanel>
    </div>
  );
}

// ─── Embedded Module (used by AdminPage sidebar) ──────────────────────────────

export function ISOModule({ activeTab }: { activeTab: ISOTab }) {
  return (
    <>
      {activeTab === "dashboard" && <DashboardTab />}
      {activeTab === "documents" && <DocumentsTab />}
      {activeTab === "controls"  && <ControlsTab />}
      {activeTab === "risks"     && <RisksTab />}
      {activeTab === "incidents" && <IncidentsTab />}
      {activeTab === "audits"    && <AuditsTab />}
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ISOPage() {
  const navigate   = useNavigate();
  const [activeTab, setActiveTab] = useState<ISOTab>("dashboard");

  const tabs: { id: ISOTab; label: string; icon: React.ReactNode }[] = [
    { id: "dashboard", label: "Dashboard",     icon: <BarChart2 size={15} /> },
    { id: "documents", label: "Documents",     icon: <BookOpen size={15} /> },
    { id: "controls",  label: "Controls",      icon: <ShieldCheck size={15} /> },
    { id: "risks",     label: "Risk Register", icon: <AlertTriangle size={15} /> },
    { id: "incidents", label: "Incidents",     icon: <AlertCircle size={15} /> },
    { id: "audits",    label: "Audits",        icon: <ClipboardList size={15} /> },
  ];

  return (
    <div className="min-h-screen bg-[#101828] text-white">
      {/* Header */}
      <div className="border-b border-white/8 bg-[#0D1520]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate("/admin")}
            className="flex items-center gap-2 text-white/50 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft size={16} />
            <span className="hidden sm:inline">Admin</span>
          </button>
          <div className="h-4 w-px bg-white/15" />
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#7B1A1A]/30 border border-[#7B1A1A]/30 flex items-center justify-center">
              <ShieldCheck size={16} className="text-[#9B2A2A]" />
            </div>
            <div>
              <h1 className="text-sm font-semibold text-white">ISO 27001 ISMS</h1>
              <p className="text-xs text-white/40">Information Security Management System</p>
            </div>
          </div>
          <div className="ml-auto">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border bg-[#7B1A1A]/20 text-[#9B2A2A] border-[#7B1A1A]/30">
              <Clock size={11} />
              ISO/IEC 27001:2022
            </span>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b border-white/8 bg-[#0D1520] sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="flex gap-1 overflow-x-auto scrollbar-hide py-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? "bg-[#7B1A1A]/30 text-white border border-[#7B1A1A]/30"
                    : "text-white/50 hover:text-white hover:bg-white/5"
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        {activeTab === "dashboard" && <DashboardTab />}
        {activeTab === "documents" && <DocumentsTab />}
        {activeTab === "controls"  && <ControlsTab />}
        {activeTab === "risks"     && <RisksTab />}
        {activeTab === "incidents" && <IncidentsTab />}
        {activeTab === "audits"    && <AuditsTab />}
      </div>
    </div>
  );
}
